<template>
  <div class="compliance-health-dashboard" :class="{ 'compliance-health-embedded': embedded }">
    <!-- Header with Summary Stats -->
    <div class="dashboard-header">
      <div class="header-content">
        <div class="header-title">
          <h1><i class="fas fa-heartbeat"></i> Compliance Health</h1>
          <p class="header-subtitle">
            <span v-if="selectedProject">{{ selectedProject.name }}</span>
            <span v-else>Select a project to view compliance health</span>
          </p>
        </div>
        <div class="header-actions">
          <select v-model="projectId" @change="onProjectChange" class="project-select">
            <option value="">All Projects</option>
            <option v-for="project in projects" :key="project.id" :value="project.id">
              {{ project.name }}
            </option>
          </select>
          <button
            v-if="!showEntityDirectory && showEmbeddedAgent"
            type="button"
            class="btn btn-outline"
            title="Show the entity directory alongside the consult"
            @click="entityDirectoryOpen = true"
          >
            <i class="fas fa-list"></i>
            Browse entities
          </button>
          <button
            v-else-if="showEntityDirectory && showEmbeddedAgent && !selectedEntity"
            type="button"
            class="btn btn-outline"
            title="Hide the directory and give the consult full width"
            @click="entityDirectoryOpen = false"
          >
            <i class="fas fa-comments"></i>
            Focus consult
          </button>
          <router-link
            :to="{
              path: '/compliance-health/consult',
              query: projectId ? { project_id: projectId } : {},
            }"
            class="btn btn-primary"
            title="Open full-page Compliance Consult"
          >
            <i class="fas fa-up-right-from-square"></i>
            Full consult
          </router-link>
          <label class="include-inactive-toggle" title="Closed/sold stores keep their coverage tracked. Uncheck to show only operating stores.">
            <input type="checkbox" v-model="includeInactive" @change="onIncludeInactiveChange">
            <span>Include Inactive Stores</span>
          </label>
          <router-link
            to="/setup/compliance-client"
            class="btn btn-success"
            title="Onboard a new compliance client"
          >
            <i class="fas fa-plus-circle"></i>
            New Client
          </router-link>
          <button 
            @click="openBulkImportModal" 
            class="btn btn-primary"
            :disabled="!projectId"
            title="Bulk import entities and locations"
          >
            <i class="fas fa-file-import"></i>
            Bulk Import
          </button>
          <button @click="refreshData" :disabled="loading" class="btn btn-refresh">
            <i :class="loading ? 'fas fa-spinner fa-spin' : 'fas fa-sync-alt'"></i>
            Refresh
          </button>
        </div>
      </div>
    </div>

    <!-- Compact KPI Bar -->
    <div class="kpi-bar">
      <div class="kpi-item score-kpi" :class="getScoreClass(summary.average_score)">
        <div class="kpi-value">{{ summary.average_score || 0 }}%</div>
        <div class="kpi-label">Avg Score</div>
      </div>
      <div class="kpi-divider"></div>
      <div class="kpi-item">
        <div class="kpi-icon excellent"><i class="fas fa-check-circle"></i></div>
        <div class="kpi-data">
          <div class="kpi-value">{{ (summary.status_breakdown?.excellent || 0) + (summary.status_breakdown?.good || 0) }}</div>
          <div class="kpi-label">Compliant</div>
        </div>
      </div>
      <div class="kpi-item">
        <div class="kpi-icon warning"><i class="fas fa-exclamation-triangle"></i></div>
        <div class="kpi-data">
          <div class="kpi-value">{{ summary.status_breakdown?.needs_attention || 0 }}</div>
          <div class="kpi-label">Attention</div>
        </div>
      </div>
      <div class="kpi-item">
        <div class="kpi-icon critical"><i class="fas fa-times-circle"></i></div>
        <div class="kpi-data">
          <div class="kpi-value">{{ (summary.status_breakdown?.at_risk || 0) + (summary.status_breakdown?.critical || 0) }}</div>
          <div class="kpi-label">At Risk</div>
        </div>
      </div>
      <div class="kpi-divider"></div>
      <div class="kpi-item">
        <div class="kpi-icon total"><i class="fas fa-building"></i></div>
        <div class="kpi-data">
          <div class="kpi-value">{{ summary.total_entities || 0 }}</div>
          <div class="kpi-label">Total Entities</div>
        </div>
      </div>
      <div class="kpi-item">
        <div class="kpi-icon with-data"><i class="fas fa-check-square"></i></div>
        <div class="kpi-data">
          <div class="kpi-value">{{ summary.entities_with_data || 0 }}</div>
          <div class="kpi-label">With Data</div>
        </div>
      </div>
      <div class="kpi-item">
        <div class="kpi-icon no-data"><i class="fas fa-question-circle"></i></div>
        <div class="kpi-data">
          <div class="kpi-value">{{ summary.entities_without_data || summary.status_breakdown?.no_data || 0 }}</div>
          <div class="kpi-label">No Data</div>
        </div>
      </div>
    </div>

    <!-- No Data Warning -->
    <div v-if="summary.status === 'no_data' || (projectId && summary.total_entities === 0)" class="no-data-warning">
      <div class="warning-content">
        <i class="fas fa-exclamation-circle"></i>
        <div class="warning-text">
          <h3>Health Data Not Calculated</h3>
          <p v-if="projectId">
            No compliance health snapshots exist for this project yet. 
            Health scores are calculated daily, or an admin can trigger calculation manually.
          </p>
          <p v-else>
            Select a project to view compliance health data.
          </p>
        </div>
        <button v-if="projectId" @click="calculateHealth" :disabled="calculating" class="btn btn-calculate">
          <i :class="calculating ? 'fas fa-spinner fa-spin' : 'fas fa-calculator'"></i>
          {{ calculating ? 'Calculating...' : 'Calculate Now' }}
        </button>
      </div>
    </div>

    <!-- Review this book — deterministic ops briefing -->
    <section v-if="projectId" class="ops-briefing-panel">
      <div class="ops-briefing-header">
        <div>
          <h2><i class="fas fa-book-open"></i> Review this book</h2>
          <p class="ops-briefing-sub">
            What needs attention in {{ selectedProject?.name || 'this project' }} — pick a cohort, then ask the agent to work it.
          </p>
        </div>
        <div class="ops-briefing-actions">
          <button
            type="button"
            class="btn btn-sm btn-outline"
            :disabled="loadingOpsBriefing"
            @click="loadOpsBriefing"
          >
            <i :class="loadingOpsBriefing ? 'fas fa-spinner fa-spin' : 'fas fa-sync-alt'"></i>
            Refresh
          </button>
          <button
            type="button"
            class="btn btn-sm btn-primary"
            :disabled="!opsBriefing || sendingBriefingReport"
            @click="openBriefingReportModal(true)"
          >
            <i class="fas fa-envelope"></i>
            Send book report
          </button>
        </div>
      </div>

      <div v-if="loadingOpsBriefing && !opsBriefing" class="ops-briefing-loading">
        <i class="fas fa-spinner fa-spin"></i> Building briefing…
      </div>

      <div v-else-if="opsBriefing" class="ops-cohort-grid">
        <button
          v-for="cohort in opsCohortCards"
          :key="cohort.key"
          type="button"
          class="ops-cohort-card"
          :class="{ active: cohortFilter === cohort.key }"
          @click="applyBriefingCohort(cohort.key, cohort.statusFilter)"
        >
          <span class="cohort-count">{{ opsBriefing.cohorts?.[cohort.key]?.count || 0 }}</span>
          <span class="cohort-label">{{ cohort.label }}</span>
        </button>
      </div>

      <div v-if="opsBriefing?.suggested_actions?.length" class="ops-suggested-actions">
        <button
          v-for="action in opsBriefing.suggested_actions"
          :key="action.id"
          type="button"
          class="btn btn-sm btn-outline"
          @click="runBriefingAction(action)"
        >
          <i class="fas fa-robot"></i> {{ action.label }}
        </button>
        <button
          v-if="(opsBriefing.cohorts?.stale_outreach?.count || 0) > 0"
          type="button"
          class="btn btn-sm btn-warning"
          @click="escalateStaleToInsured"
        >
          <i class="fas fa-user-shield"></i> Escalate stale to insured
        </button>
      </div>

      <div v-if="outreachSummary" class="outreach-results">
        <div class="outreach-results-head">
          <h3><i class="fas fa-envelope-open-text"></i> Email results</h3>
          <span class="outreach-window">{{ outreachWindowLabel }}</span>
        </div>
        <p class="outreach-results-text">{{ outreachSummary.summary_text }}</p>
        <div v-if="outreachSummary.emails_sent" class="outreach-metrics">
          <span><strong>{{ outreachSummary.emails_sent }}</strong> emails</span>
          <span><strong>{{ outreachSummary.stores_emailed }}</strong> stores</span>
          <span><strong>{{ outreachDelivered }}</strong> delivered</span>
          <span><strong>{{ outreachSummary.email_states?.opened || 0 }}</strong> opened</span>
          <span :class="{ 'metric-problem': outreachSummary.bounced?.count }">
            <strong>{{ outreachSummary.bounced?.count || 0 }}</strong> bounced or spam
          </span>
          <span><strong>{{ outreachSummary.certificates_received }}</strong> new certificates</span>
        </div>
        <div class="outreach-actions">
          <button
            v-if="outreachSummary.needs_agent_email_total"
            type="button"
            class="btn btn-sm btn-outline"
            :class="{ active: cohortFilter === 'needs_agent_email' }"
            @click="applyBriefingCohort('needs_agent_email')"
          >
            <i class="fas fa-user-tie"></i>
            View all {{ outreachSummary.needs_agent_email_total }} stores needing an agent email
          </button>
          <button
            type="button"
            class="btn btn-sm btn-outline"
            @click="triggerAgentQuery('How did the emails do since I last checked?')"
          >
            <i class="fas fa-robot"></i> Ask the agent about email results
          </button>
          <router-link
            class="btn btn-sm btn-outline"
            :to="`/projects/${projectId}/compliance-health/email-delivery`"
          >
            <i class="fas fa-chart-bar"></i> See if they got through
          </router-link>
        </div>
      </div>

      <div v-if="cohortFilter" class="ops-active-filter">
        <span>Directory filtered: <strong>{{ activeCohortLabel }}</strong></span>
        <button type="button" class="btn-link" @click="clearCohortFilter">Clear filter</button>
      </div>
    </section>

    <!-- Book report modal -->
    <div v-if="showBriefingReportModal" class="modal-overlay" @click.self="closeBriefingReportModal">
      <div class="modal-dialog briefing-report-modal">
        <div class="modal-header">
          <h3><i class="fas fa-file-export"></i> Send book report</h3>
          <button type="button" class="btn-close" @click="closeBriefingReportModal">
            <i class="fas fa-times"></i>
          </button>
        </div>
        <div class="modal-body">
          <p class="briefing-report-lead">
            Client-facing summary of the five ops cohorts for {{ selectedProject?.name }}.
            Dry-run first, then send to explicit recipient emails.
          </p>
          <div class="form-group">
            <label>Recipient emails (comma-separated)</label>
            <input
              v-model="briefingReportRecipients"
              type="text"
              class="form-input"
              placeholder="client@example.com, ops@example.com"
            >
          </div>
          <div v-if="briefingReportDryRun?.body_preview" class="briefing-preview">
            <h4>Preview</h4>
            <pre>{{ briefingReportDryRun.body_preview }}</pre>
          </div>
        </div>
        <div class="modal-footer">
          <button
            type="button"
            class="btn btn-outline"
            :disabled="sendingBriefingReport"
            @click="openBriefingReportModal(true)"
          >
            Dry run
          </button>
          <button
            type="button"
            class="btn btn-primary"
            :disabled="sendingBriefingReport || !briefingReportRecipients.trim()"
            @click="sendBriefingReport"
          >
            <i v-if="sendingBriefingReport" class="fas fa-spinner fa-spin"></i>
            Send report
          </button>
        </div>
      </div>
    </div>

    <!-- Main Content Grid - Entity directory + detail + agent -->
    <div
      class="dashboard-grid"
      :class="{ 'dashboard-grid--consult-only': consultFocusLayout }"
    >
      <!-- Left: Entity Directory (SQL-paginated) -->
      <div v-if="showEntityDirectory" class="entity-directory-panel">
        <div class="directory-header">
          <h3>
            Entities
            <span v-if="entityTotal" class="directory-count">{{ entityTotal.toLocaleString() }}</span>
          </h3>
        </div>

        <div class="directory-controls">
          <div class="directory-search">
            <i class="fas fa-search"></i>
            <input
              v-model="searchQuery"
              type="search"
              placeholder="Search entities..."
              @input="debouncedSearch"
            >
          </div>
          <select v-model="statusFilter" class="directory-filter" @change="loadEntities(true)">
            <option value="">All statuses</option>
            <option value="excellent">Excellent</option>
            <option value="good">Good</option>
            <option value="needs_attention">Needs attention</option>
            <option value="at_risk">At risk</option>
            <option value="critical">Critical</option>
            <option value="no_data">No data</option>
          </select>
        </div>

        <div v-if="loadingEntities && entities.length === 0" class="directory-loading">
          <i class="fas fa-spinner fa-spin"></i> Loading entities...
        </div>

        <div v-else-if="entities.length === 0" class="directory-empty">
          <p v-if="projectId">No entities match your filters.</p>
          <p v-else>Select a project to browse entities.</p>
        </div>

        <div v-else class="directory-list">
          <button
            v-for="entity in entities"
            :key="entity.subject_id"
            type="button"
            class="directory-row"
            :class="{
              active: selectedEntity?.subject_id === entity.subject_id,
              inactive: entity.is_inactive,
            }"
            @click="selectEntity(entity)"
          >
            <div class="row-main">
              <span class="row-name">{{ entity.entity_name }}</span>
              <span class="row-score" :class="getScoreClass(entity.overall_score)">
                {{ entity.overall_score }}%
              </span>
            </div>
            <p v-if="entity.top_issue" class="row-issue">{{ entity.top_issue }}</p>
            <p v-else-if="entity.total_requirements === 0" class="row-issue row-issue--muted">
              No compliance data yet
            </p>
            <div class="row-actions">
              <button
                v-if="entity.subject_id"
                type="button"
                class="row-action-btn"
                title="Open full compliance record"
                @click.stop="openEntityRecord(entity.subject_id, entity.project_id)"
              >
                <i class="fas fa-book-open"></i>
              </button>
            </div>
          </button>

          <button
            v-if="hasMoreEntities"
            type="button"
            class="directory-load-more"
            :disabled="loadingMore"
            @click="loadMoreEntities"
          >
            <i v-if="loadingMore" class="fas fa-spinner fa-spin"></i>
            <span v-else>Load more ({{ entities.length }} of {{ entityTotal.toLocaleString() }})</span>
          </button>
        </div>
      </div>

      <!-- Center: live consult (default) or entity detail -->
      <div class="detail-panel" :class="{ 'detail-panel--agent': showEmbeddedAgent }">
        <div v-if="showEmbeddedAgent" class="agent-pane">
          <ComplianceConsultChat
            ref="consultChatRef"
            :project-id="projectId || null"
            :summary="summary"
            :is-sidebar="false"
            initial-depth="quick"
            :initial-query="pendingInitialQuery"
            @filter-entities="handleFilterEntities"
            @select-entities="handleSelectEntities"
            @notify="handleNotify"
            @show-results="handleQueryResults"
            @refresh-data="refreshData"
            @create-entity="handleCreateEntity"
            @show-entity-details="handleShowEntityDetails"
            @show-document="handleShowDocument"
            @open-full-consult="goToFullConsult"
          />
        </div>

        <!-- Entity Detail View -->
        <div v-if="activeView === 'detail' && selectedEntity && entityDetail">
          <div class="entity-detail">
            <button
              type="button"
              class="btn btn-sm btn-outline back-to-agent"
              @click="clearSelectedEntity"
            >
              <i class="fas fa-arrow-left"></i>
              Back to agent
            </button>
            <div class="detail-header">
              <div class="detail-title">
                <h2>{{ entityDetail.entity_name }}</h2>
                <span class="status-badge large" :class="getStatusClass(entityDetail.status)">
                  {{ formatStatus(entityDetail.status) }}
                </span>
              </div>
              <div class="detail-score">
                <div class="score-circle" :class="getScoreClass(entityDetail.overall_score)">
                  {{ entityDetail.overall_score }}%
                </div>
              </div>
            </div>

            <!-- Coverage Breakdown -->
            <div class="detail-section">
              <h4><i class="fas fa-shield-alt"></i> Coverage Breakdown</h4>
              <div v-if="Object.keys(entityDetail.coverage_scores || {}).length > 0" class="coverage-grid">
                <div 
                  v-for="(data, type) in entityDetail.coverage_scores" 
                  :key="type"
                  class="coverage-item clickable"
                  :class="getStatusClass(data.status)"
                  @click="viewCoverageDocuments(type)"
                  :title="`Click to view documents for ${type}`"
                >
                  <div class="coverage-type">
                    {{ type }}
                    <i class="fas fa-file-alt coverage-doc-icon"></i>
                  </div>
                  <div class="coverage-score">{{ data.score }}%</div>
                  <div class="coverage-status">{{ data.status }}</div>
                  <div class="coverage-counts">
                    {{ data.passing }}/{{ data.total }} passing
                  </div>
                </div>
              </div>
              <div v-else class="empty-state-inline">
                <i class="fas fa-exclamation-circle"></i>
                <p>No coverage data available. This entity has not been analyzed yet.</p>
                <p class="hint">Upload insurance documents and run a comprehensive check to generate coverage data.</p>
              </div>
            </div>

            <!-- Root Causes -->
            <div class="detail-section">
              <h4><i class="fas fa-search"></i> Root Causes</h4>
              <div v-if="entityDetail.root_causes?.length" class="root-causes-list">
                <div 
                  v-for="(cause, index) in entityDetail.root_causes" 
                  :key="index"
                  class="root-cause-item"
                >
                  <div class="cause-header">
                    <span class="cause-type" :class="getCausePriorityClass(cause.priority)">
                      {{ formatCauseType(cause.type) }}
                    </span>
                    <span class="cause-impact">
                      {{ cause.cascading_failures }} affected
                    </span>
                  </div>
                  <div class="cause-title">{{ cause.title }}</div>
                  <div class="cause-resolution">
                    <i class="fas fa-lightbulb"></i>
                    {{ cause.resolution }}
                  </div>
                </div>
              </div>
              <div v-else-if="entityDetail.total_requirements === 0" class="empty-state-inline warning">
                <i class="fas fa-file-upload"></i>
                <p><strong>No compliance data found</strong></p>
                <p class="hint">This entity requires insurance document analysis. Upload COI documents and run a comprehensive compliance check.</p>
              </div>
              <div v-else class="empty-state-inline success">
                <i class="fas fa-check-circle"></i>
                <p>No issues detected. All requirements are passing.</p>
              </div>
            </div>

            <!-- Temporal Projections -->
            <div v-if="entityDetail.upcoming_expirations?.length" class="detail-section">
              <h4><i class="fas fa-calendar-alt"></i> Upcoming Expirations</h4>
              <div class="expirations-list">
                <div 
                  v-for="(exp, index) in entityDetail.upcoming_expirations" 
                  :key="index"
                  class="expiration-item"
                  :class="{ urgent: exp.days_until <= 7 }"
                >
                  <div class="exp-type">{{ exp.coverage_type }}</div>
                  <div class="exp-date">{{ formatDate(exp.expires) }}</div>
                  <div class="exp-days" :class="{ critical: exp.days_until <= 7, warning: exp.days_until <= 30 }">
                    {{ exp.days_until }} days
                  </div>
                </div>
              </div>
            </div>

            <!-- Score Projections -->
            <div class="detail-section projections">
              <h4><i class="fas fa-chart-line"></i> Score Projections</h4>
              <div class="projection-bars">
                <div class="projection-item">
                  <span class="projection-label">Now</span>
                  <div class="projection-bar">
                    <div class="projection-fill" :style="{ width: entityDetail.overall_score + '%' }" :class="getScoreClass(entityDetail.overall_score)"></div>
                  </div>
                  <span class="projection-value">{{ entityDetail.overall_score }}%</span>
                </div>
                <div class="projection-item">
                  <span class="projection-label">30 Days</span>
                  <div class="projection-bar">
                    <div class="projection-fill" :style="{ width: entityDetail.score_30_days + '%' }" :class="getScoreClass(entityDetail.score_30_days)"></div>
                  </div>
                  <span class="projection-value">{{ entityDetail.score_30_days }}%</span>
                </div>
                <div class="projection-item">
                  <span class="projection-label">60 Days</span>
                  <div class="projection-bar">
                    <div class="projection-fill" :style="{ width: entityDetail.score_60_days + '%' }" :class="getScoreClass(entityDetail.score_60_days)"></div>
                  </div>
                  <span class="projection-value">{{ entityDetail.score_60_days }}%</span>
                </div>
                <div class="projection-item">
                  <span class="projection-label">90 Days</span>
                  <div class="projection-bar">
                    <div class="projection-fill" :style="{ width: entityDetail.score_90_days + '%' }" :class="getScoreClass(entityDetail.score_90_days)"></div>
                  </div>
                  <span class="projection-value">{{ entityDetail.score_90_days }}%</span>
                </div>
              </div>
            </div>

            <!-- AI Analysis Section -->
            <div class="detail-section ai-section">
              <div class="section-header-with-action">
                <h4><i class="fas fa-robot"></i> AI Analysis</h4>
                <button 
                  v-if="!entityDetail.executive_summary && entityDetail.total_requirements > 0"
                  @click="generateAIAnalysis"
                  :disabled="generatingAI"
                  class="btn btn-sm btn-ai"
                >
                  <i :class="generatingAI ? 'fas fa-spinner fa-spin' : 'fas fa-magic'"></i>
                  {{ generatingAI ? 'Analyzing...' : 'Generate Analysis' }}
                </button>
                <span v-if="entityDetail.llm_model_used" class="model-badge">
                  <i class="fas fa-microchip"></i> {{ entityDetail.llm_model_used }}
                </span>
              </div>
              
              <div v-if="entityDetail.executive_summary" class="ai-summary">
                <p>{{ entityDetail.executive_summary }}</p>
                <p v-if="entityDetail.trend_analysis" class="trend-note">
                  <i class="fas fa-chart-line"></i> {{ entityDetail.trend_analysis }}
                </p>
              </div>
              
              <div v-else-if="entityDetail.total_requirements === 0" class="ai-placeholder">
                <i class="fas fa-file-upload"></i>
                <p>Upload and analyze documents to enable AI analysis.</p>
              </div>
              
              <div v-else class="ai-placeholder">
                <i class="fas fa-lightbulb"></i>
                <p>Click "Generate Analysis" to get AI-powered insights and recommendations.</p>
              </div>
            </div>

            <!-- Priority Actions -->
            <div v-if="entityDetail.priority_actions?.length" class="detail-section">
              <h4><i class="fas fa-tasks"></i> Recommended Actions</h4>
              <div class="actions-list">
                <div 
                  v-for="(action, index) in entityDetail.priority_actions" 
                  :key="index"
                  class="action-item"
                >
                  <div class="action-priority" :class="action.urgency?.toLowerCase()">
                    {{ action.priority }}
                  </div>
                  <div class="action-content">
                    <div class="action-text">{{ action.action }}</div>
                    <div class="action-meta">
                      <span v-if="action.owner" class="action-owner">
                        <i class="fas fa-user"></i> {{ action.owner }}
                      </span>
                      <span class="action-urgency" :class="action.urgency?.toLowerCase()">
                        {{ action.urgency }}
                      </span>
                      <span v-if="action.impact" class="action-impact">{{ action.impact }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Unified Action Panel - Results & Email Draft -->
        <div v-else-if="activeView === 'action-panel'" class="action-panel">
          <!-- Panel Header with Tabs -->
          <div class="action-panel-header">
            <div class="panel-tabs">
              <button 
                class="panel-tab" 
                :class="{ active: actionPanelTab === 'results' }"
                @click="actionPanelTab = 'results'"
                :disabled="!queryResultsData"
              >
                <i class="fas fa-search"></i>
                Results
                <span v-if="queryResultsData?.entities?.length" class="tab-badge">
                  {{ queryResultsData.entities.length }}
                </span>
              </button>
              <button 
                class="panel-tab" 
                :class="{ active: actionPanelTab === 'draft' }"
                @click="actionPanelTab = 'draft'"
                :disabled="!notificationPreviewData"
              >
                <i class="fas fa-envelope"></i>
                Email Draft
                <span v-if="notificationPreviewData?.entityIds?.length" class="tab-badge">
                  {{ notificationPreviewData.entityIds.length }}
                </span>
              </button>
            </div>
            <button @click="closeActionPanel" class="btn btn-icon" title="Close panel">
              <i class="fas fa-times"></i>
            </button>
          </div>

          <!-- Results Tab Content -->
          <div v-if="actionPanelTab === 'results'" class="tab-content results-content">
            <div v-if="queryResultsData" class="results-inner">
              <div class="results-summary">
                <div class="summary-stat" v-if="queryResultsData.coverageType">
                  <i class="fas fa-shield-alt"></i>
                  <span class="stat-label">Coverage:</span>
                  <span class="stat-value">{{ queryResultsData.coverageType }}</span>
                </div>
                <div class="summary-stat">
                  <i class="fas fa-building"></i>
                  <span class="stat-label">Found:</span>
                  <span class="stat-value">{{ queryResultsData.entities?.length || 0 }} entities</span>
                </div>
                <div class="summary-stat" v-if="selectedResultEntityIds.length > 0">
                  <i class="fas fa-check-square"></i>
                  <span class="stat-label">Selected:</span>
                  <span class="stat-value">{{ selectedResultEntityIds.length }}</span>
                </div>
              </div>

              <div class="results-list">
                <div 
                  v-for="entity in queryResultsData.entities" 
                  :key="entity.entity_id || entity.subject_id"
                  class="result-entity-wrapper"
                  :class="{ expanded: expandedEntityId === entity.subject_id }"
                >
                  <div 
                    class="result-entity-card"
                    :class="[getResultStatusClass(entity), { selected: selectedResultEntityIds.includes(entity.subject_id) }]"
                  >
                    <div class="entity-main">
                      <div class="entity-checkbox">
                        <input 
                          type="checkbox" 
                          :checked="selectedResultEntityIds.includes(entity.subject_id)"
                          @click.stop
                          @change="toggleEntitySelection(entity.subject_id)"
                        >
                      </div>
                      <div class="entity-info" @click="toggleEntityExpansion(entity.subject_id, $event)">
                        <div class="entity-header-row">
                          <h4 class="entity-name">{{ entity.entity_name || entity.name || `Entity #${entity.subject_id || entity.entity_id}` }}</h4>
                          <!-- Score Badge -->
                          <div v-if="entity.score !== undefined && entity.score !== null" class="score-badge" :class="getScoreClass(entity.score)">
                            {{ entity.score }}%
                            <span v-if="entity.trend" class="trend-indicator" :class="getTrendClass(entity.trend)">
                              <i :class="getTrendIcon(entity.trend)"></i>
                            </span>
                          </div>
                        </div>
                        <div class="entity-meta">
                          <span v-if="entity.subject_id" class="meta-item meta-id">
                            ID: {{ entity.subject_id }}
                          </span>
                          <span class="meta-item status-badge" :class="(entity.status || entity.overall_status || '').toLowerCase().replace(/[^a-z]/g, '_')">
                            {{ formatEntityStatus(entity.status || entity.overall_status) || 'Unknown' }}
                          </span>
                          <span
                            v-if="entity.is_inactive || entity.current_status === 'INACTIVE'"
                            class="meta-item inactive-badge"
                            title="Store is closed/sold - coverage still tracked"
                          >
                            <i class="fas fa-store-slash"></i> Inactive
                          </span>
                          <!-- Coverage Summary -->
                          <span v-if="entity.coverage_summary" class="meta-item coverage-summary" :class="getCoverageSummaryClass(entity.coverage_summary)">
                            <i class="fas fa-shield-alt"></i>
                            {{ entity.coverage_summary.compliant }}/{{ entity.coverage_summary.total }}
                          </span>
                          <!-- Requirements Summary -->
                          <span v-if="entity.total_requirements" class="meta-item reqs-summary">
                            <i class="fas fa-clipboard-check"></i>
                            {{ entity.passing_requirements || 0 }}/{{ entity.total_requirements }}
                          </span>
                        </div>
                      </div>
                      <!-- Docs & Analysis Button -->
                      <button
                        v-if="entity.subject_id"
                        class="expand-btn docs-btn"
                        @click.stop="openDocsModal(entity.subject_id, entity.entity_name || entity.name)"
                        title="View documents &amp; analysis history"
                      >
                        <i class="fas fa-folder-open"></i>
                      </button>
                      <button
                        v-if="entity.subject_id"
                        class="expand-btn record-btn"
                        @click.stop="openEntityRecord(entity.subject_id, entity.project_id)"
                        title="View full compliance record and coverage history"
                      >
                        <i class="fas fa-book-open"></i>
                      </button>
                      <!-- Expand/Collapse Button -->
                      <button 
                        class="expand-btn"
                        @click.stop="toggleEntityExpansion(entity.subject_id)"
                        :title="expandedEntityId === entity.subject_id ? 'Collapse details' : 'View full details'"
                      >
                        <i :class="expandedEntityId === entity.subject_id ? 'fas fa-chevron-up' : 'fas fa-chevron-down'"></i>
                      </button>
                    </div>
                    <!-- Failing Requirements Section -->
                    <div class="entity-issues" v-if="entity.failing_requirements?.length">
                      <div class="issues-header">
                        <i class="fas fa-exclamation-triangle"></i>
                        <span>{{ entity.failing_requirements.length }} issue{{ entity.failing_requirements.length > 1 ? 's' : '' }}</span>
                      </div>
                      <div class="issues-list">
                        <span 
                          v-for="(req, idx) in entity.failing_requirements.slice(0, 3)" 
                          :key="idx"
                          class="issue-tag"
                          :class="getIssueSeverityClass(req)"
                        >
                          {{ formatIssueLabel(req) }}
                        </span>
                        <span v-if="entity.failing_requirements.length > 3" class="more-issues">
                          +{{ entity.failing_requirements.length - 3 }} more
                        </span>
                      </div>
                    </div>
                    <!-- Coverage Details (if available) -->
                    <div v-if="entity.coverage_summary?.details && Object.keys(entity.coverage_summary.details).length > 0" class="coverage-details">
                      <div 
                        v-for="(coverage, coverageType) in entity.coverage_summary.details" 
                        :key="coverageType"
                        class="coverage-chip"
                        :class="getCoverageChipClass(coverage)"
                        :title="`${coverageType}: ${coverage.score}% - ${coverage.passing}/${coverage.total} passing`"
                      >
                        <span class="coverage-type">{{ coverageType }}</span>
                        <span class="coverage-status-icon">
                          <i :class="coverage.score >= 75 ? 'fas fa-check' : 'fas fa-times'"></i>
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  <!-- Phase 2: Expanded Detail Panel -->
                  <EntityDetailPanel
                    v-if="expandedEntityId === entity.subject_id"
                    :detail="expandedEntityDetail"
                    :loading="loadingEntityDetail"
                    :error="entityDetailError"
                    @retry="retryEntityDetail"
                    @archived="handleEntityArchived"
                    @add-store="handleAddStore"
                    @view-docs="openDocsModal"
                  />
                </div>
                
                <div v-if="!queryResultsData.entities?.length" class="no-results">
                  <i class="fas fa-info-circle"></i>
                  <p>{{ queryResultsData.message || 'No matching entities found.' }}</p>
                </div>
              </div>

              <div class="results-actions" v-if="queryResultsData.entities?.length > 0">
                <div class="select-actions">
                  <button @click="selectAllResults" class="btn btn-sm">
                    <i class="fas fa-check-double"></i> All
                  </button>
                  <button @click="clearResultSelection" class="btn btn-sm">
                    <i class="fas fa-times"></i> None
                  </button>
                </div>
                <div class="action-buttons">
                  <button 
                    @click="downloadResultsAsCsv"
                    class="btn btn-secondary"
                    title="Download as CSV"
                  >
                    <i class="fas fa-download"></i>
                    CSV
                  </button>
                  <button 
                    @click="startDraftFromResults"
                    :disabled="selectedResultEntityIds.length === 0"
                    class="btn btn-primary"
                  >
                    <i class="fas fa-envelope"></i>
                    Draft Email ({{ selectedResultEntityIds.length }})
                  </button>
                </div>
              </div>
            </div>
            <div v-else class="empty-tab">
              <i class="fas fa-search"></i>
              <p>Ask the agent a question to see results here</p>
            </div>
          </div>

          <!-- Draft Tab Content -->
          <div v-if="actionPanelTab === 'draft'" class="tab-content draft-content">
            <div v-if="notificationPreviewData" class="draft-inner">
              <!-- Recipients — show the address each email is going to, and
                   name the ones with no address instead of hiding them. -->
              <div class="draft-recipients">
                <div class="recipients-header">
                  <label><i class="fas fa-users"></i> To:</label>
                  <div class="recipients-display">
                    <span class="recipient-count">
                      {{ draftRecipientCount }}
                      {{ draftRecipientCount === 1 ? 'recipient' : 'recipients' }}
                    </span>
                    <span v-if="draftMissingEmailCount" class="recipient-warning">
                      {{ draftMissingEmailCount }} with no address
                    </span>
                    <span v-if="loadingDraftRecipients" class="recipient-loading">
                      <i class="fas fa-spinner fa-spin"></i> resolving addresses
                    </span>
                    <button
                      v-if="queryResultsData?.entities?.length"
                      @click="actionPanelTab = 'results'"
                      class="btn btn-link btn-sm"
                    >
                      Edit selection
                    </button>
                  </div>
                </div>

                <ul v-if="draftRecipients.length" class="recipient-address-list">
                  <li
                    v-for="recipient in draftRecipients"
                    :key="recipient.subject_id"
                    class="recipient-address"
                    :class="{ 'no-address': !recipient.recipient_email }"
                  >
                    <span class="recipient-entity">{{ recipient.entity_name }}</span>
                    <span v-if="recipient.recipient_email" class="recipient-email">
                      {{ recipient.recipient_email }}
                      <small>{{ recipient.route_label }}</small>
                    </span>
                    <span v-else class="recipient-email is-missing">
                      No agent or subcontractor email — will be skipped
                      <button
                        v-if="projectId"
                        class="btn btn-link btn-sm"
                        @click="openEntityRecord(recipient.subject_id)"
                      >
                        Add contact
                      </button>
                    </span>
                  </li>
                </ul>
                <p v-else-if="!loadingDraftRecipients" class="recipient-address-empty">
                  <i class="fas fa-triangle-exclamation"></i>
                  No addresses resolved for this selection. Add an agent or
                  subcontractor email before sending.
                </p>
              </div>

              <!-- AI Controls -->
              <div class="ai-controls-compact">
                <select v-model="emailTone" class="control-select">
                  <option value="professional">Professional</option>
                  <option value="friendly">Friendly</option>
                  <option value="urgent">Urgent</option>
                  <option value="firm">Firm</option>
                </select>
                <select v-model="emailUrgency" class="control-select">
                  <option value="low">Low Priority</option>
                  <option value="normal">Normal</option>
                  <option value="high">High Priority</option>
                  <option value="critical">Critical</option>
                </select>
                <button 
                  @click="generateAIEmail" 
                  :disabled="generatingEmail" 
                  class="btn btn-ai btn-sm"
                >
                  <i :class="generatingEmail ? 'fas fa-spinner fa-spin' : 'fas fa-magic'"></i>
                  {{ generatingEmail ? '' : 'AI Generate' }}
                </button>
              </div>

              <!-- Subject -->
              <div class="draft-field">
                <label>Subject:</label>
                <input 
                  v-model="notificationPreviewData.subject" 
                  type="text"
                  class="subject-input"
                  placeholder="Email subject..."
                >
              </div>

              <!-- Message Body -->
              <div class="draft-field message-field">
                <label>Message:</label>
                <textarea 
                  v-model="notificationPreviewData.customMessage" 
                  class="message-editor"
                  placeholder="Write your message or click AI Generate..."
                  rows="8"
                ></textarea>
              </div>

              <!-- Send Actions -->
              <div class="draft-actions">
                <div v-if="notificationPreviewData.aiGenerated" class="ai-badge-small">
                  <i class="fas fa-robot"></i> AI Generated
                </div>
                <button
                  @click="confirmNotification"
                  :disabled="sendingNotification || !draftSendableCount"
                  class="btn btn-primary btn-lg"
                >
                  <i :class="sendingNotification ? 'fas fa-spinner fa-spin' : 'fas fa-paper-plane'"></i>
                  {{ sendingNotification ? 'Sending...' : `Send for ${draftSendableCount} ${draftSendableCount === 1 ? 'store' : 'stores'}` }}
                </button>
              </div>
            </div>
            <div v-else class="empty-tab">
              <i class="fas fa-envelope"></i>
              <p>Select entities from Results to draft an email</p>
            </div>
          </div>
        </div>

        <!-- Legacy Notification Preview (for backward compatibility) -->
        <div v-else-if="activeView === 'notification-preview' && notificationPreviewData" class="notification-draft">
          <div class="draft-header">
            <div class="header-left">
              <h2><i class="fas fa-paper-plane"></i> Notification Draft</h2>
              <p>Review and edit the message before sending</p>
            </div>
            <div class="header-right">
              <button @click="activeView = 'detail'" class="btn btn-secondary">
                <i class="fas fa-times"></i> Cancel
              </button>
            </div>
          </div>

          <!-- AI Generation Controls -->
          <div class="ai-controls">
            <div class="ai-options">
              <div class="option-group">
                <label>Tone:</label>
                <select v-model="emailTone">
                  <option value="professional">Professional</option>
                  <option value="friendly">Friendly</option>
                  <option value="urgent">Urgent</option>
                  <option value="formal">Formal</option>
                  <option value="firm">Firm</option>
                </select>
              </div>
              <div class="option-group">
                <label>Urgency:</label>
                <select v-model="emailUrgency">
                  <option value="low">Low</option>
                  <option value="normal">Normal</option>
                  <option value="high">High</option>
                  <option value="critical">Critical</option>
                </select>
              </div>
              <div class="option-group flex-1">
                <label>Custom Instructions (optional):</label>
                <input 
                  v-model="emailInstructions" 
                  type="text" 
                  placeholder="e.g., Be brief, mention deadline is Jan 25th..."
                  class="instructions-input"
                >
              </div>
            </div>
            <button 
              @click="generateAIEmail" 
              :disabled="generatingEmail" 
              class="btn btn-ai"
              title="Generate email using AI based on entity compliance data"
            >
              <i v-if="generatingEmail" class="fas fa-spinner fa-spin"></i>
              <i v-else class="fas fa-magic"></i>
              {{ generatingEmail ? 'Generating...' : 'Generate with AI' }}
            </button>
          </div>

          <div class="draft-envelope">
            <div class="envelope-row">
              <label>To:</label>
              <div class="envelope-recipients">
                <div v-if="draftRecipients.length" class="recipient-details">
                  <div
                    v-for="recipient in draftRecipients"
                    :key="recipient.subject_id"
                    class="recipient-tag"
                    :class="{ 'no-address': !recipient.recipient_email }"
                  >
                    <i class="fas fa-building"></i>
                    <span>{{ recipient.entity_name }}</span>
                    <small v-if="recipient.recipient_email">{{ recipient.recipient_email }}</small>
                    <small v-else class="is-missing">no email on file</small>
                    <small v-if="recipient.cc_email">CC: {{ recipient.cc_email }}</small>
                  </div>
                </div>
                <div v-else-if="notificationPreviewData.entityIds?.length <= 10" class="recipient-details">
                  <div v-for="id in notificationPreviewData.entityIds" :key="id" class="recipient-tag">
                    <i class="fas fa-building"></i>
                    <span>{{ getEntityName(id) }}</span>
                  </div>
                </div>
                <div v-else class="recipient-summary">
                  <strong>{{ notificationPreviewData.entityIds?.length }}</strong> recipients selected
                  <span class="category-badge">{{ notificationPreviewData.template?.replace(/_/g, ' ') }}</span>
                </div>
              </div>
            </div>

            <div class="envelope-row">
              <label>From:</label>
              <div class="from-field">Praetorian AI Compliance System</div>
            </div>

            <label
              v-if="notificationPreviewData.recipientPreference !== 'insured'"
              class="copy-subcontractor-control"
            >
              <input
                v-model="notificationPreviewData.ccSubcontractor"
                type="checkbox"
                @change="hydrateDraftRecipients"
              >
              <span>
                <strong>Copy subcontractor when an agent is available</strong>
                <small>Turn off for agent-only delivery.</small>
              </span>
            </label>

            <div class="envelope-row subject-row">
              <label>Subject: <span class="edit-hint">(editable)</span></label>
              <input 
                v-model="notificationPreviewData.subject" 
                type="text"
                class="subject-input"
                placeholder="Email subject line..."
              >
            </div>

            <div class="envelope-body">
              <div class="body-label">
                <i class="fas fa-edit"></i>
                <span>Message Body</span>
                <span class="edit-hint">(click to edit)</span>
              </div>
              <textarea 
                v-model="notificationPreviewData.customMessage" 
                rows="15" 
                class="message-editor"
                placeholder="Write your message here or click 'Generate with AI' above..."
              ></textarea>
            </div>
            
            <div v-if="notificationPreviewData.aiGenerated" class="ai-badge">
              <i class="fas fa-robot"></i> AI Generated 
              <span class="generation-time">in {{ notificationPreviewData.generationTime }}ms</span>
            </div>
          </div>

          <div class="draft-actions">
            <div class="warning-note">
              <i class="fas fa-info-circle"></i>
              <template v-if="notificationPreviewData.recipientPreference === 'insured'">
                Sending this will notify <strong>insured primary contacts</strong>
                for <strong>{{ notificationPreviewData.entityIds?.length }}</strong> entities
                (escalation — not the agent).
              </template>
              <template v-else>
                Sending this will notify <strong>{{ notificationPreviewData.entityIds?.length }}</strong>
                records immediately.
                <span v-if="notificationSubcontractorFallbacks">
                  <strong>{{ notificationSubcontractorFallbacks }}</strong>
                  {{ notificationSubcontractorFallbacks === 1 ? 'has' : 'have' }}
                  no reachable insurance agent and will go to the subcontractor
                  with instructions to forward it or provide the agent’s details.
                </span>
                <span v-if="notificationUnavailableRecipients">
                  <strong>{{ notificationUnavailableRecipients }}</strong>
                  {{ notificationUnavailableRecipients === 1 ? 'has' : 'have' }}
                  no agent or subcontractor email and will be skipped.
                </span>
              </template>
            </div>
            <button @click="confirmNotification" :disabled="sendingNotification" class="btn btn-primary btn-lg">
              <i v-if="sendingNotification" class="fas fa-spinner fa-spin"></i>
              <i v-else class="fas fa-paper-plane"></i>
              {{ sendingNotification ? 'Sending...' : 'Send Notifications Now' }}
            </button>
          </div>
        </div>
      </div>

    </div>
  </div>
  
  <!-- Entity Documents & Analysis Modal -->
  <EntityDocumentsModal
    :show="showDocsModal"
    :subject-id="docsModalSubjectId"
    :entity-name="docsModalEntityName"
    @close="showDocsModal = false"
  />

  <!-- Agent outreach goes out in planned batches, never the chat preview. -->
  <ComplianceOutreachDrawer
    :payload="outreachPayload"
    :project-id="Number(projectId) || null"
    :scope-label="selectedProject ? `${selectedProject.name} records` : 'portfolio records'"
    @close="outreachPayload = null"
    @show-entity-details="openEntityRecord"
  />

  <!-- Create Entity Modal -->
  <div v-if="showCreateEntityModal" class="modal-overlay" @click.self="cancelCreateEntity">
    <div class="modal-dialog create-entity-modal">
      <div class="modal-header">
        <h3><i class="fas fa-building-circle-plus"></i> Create New Entity</h3>
        <button @click="cancelCreateEntity" class="btn-close">
          <i class="fas fa-times"></i>
        </button>
      </div>
      
      <div class="modal-body">
        <div class="form-group">
          <label>Entity Name <span class="required">*</span></label>
          <input 
            v-model="createEntityForm.entity_name" 
            type="text" 
            class="form-input"
            placeholder="Enter company or person name"
            autofocus
          />
        </div>
        
        <div class="form-row">
          <div class="form-group">
            <label>Entity Type</label>
            <select v-model="createEntityForm.entity_type" class="form-select">
              <option value="COMPANY">Company</option>
              <option value="PERSON">Person</option>
              <option value="AGENCY">Agency</option>
            </select>
          </div>
          
          <div class="form-group">
            <label>Email</label>
            <input 
              v-model="createEntityForm.email" 
              type="email" 
              class="form-input"
              placeholder="contact@example.com"
            />
          </div>
        </div>
        
        <div class="form-group">
          <label>Phone</label>
          <input 
            v-model="createEntityForm.phone" 
            type="tel" 
            class="form-input"
            placeholder="(555) 123-4567"
          />
        </div>
        
        <div class="form-section">
          <label class="section-label">Address (Optional)</label>
          <div class="form-group">
            <input 
              v-model="createEntityForm.address.line1" 
              type="text" 
              class="form-input"
              placeholder="Street address"
            />
          </div>
          <div class="form-row">
            <div class="form-group flex-2">
              <input 
                v-model="createEntityForm.address.city" 
                type="text" 
                class="form-input"
                placeholder="City"
              />
            </div>
            <div class="form-group">
              <input 
                v-model="createEntityForm.address.state" 
                type="text" 
                class="form-input"
                placeholder="State"
                maxlength="2"
              />
            </div>
            <div class="form-group">
              <input 
                v-model="createEntityForm.address.postal_code" 
                type="text" 
                class="form-input"
                placeholder="ZIP"
              />
            </div>
          </div>
        </div>
        
        <!-- Location/Store Section -->
        <div class="form-section">
          <h4 class="section-title">
            <i class="fas fa-store"></i> Location/Store (Optional)
          </h4>
          <div class="form-row">
            <div class="form-group">
              <label>Store Number</label>
              <input 
                v-model="createEntityForm.store_number" 
                type="text" 
                class="form-input"
                placeholder="e.g., 101006"
              />
            </div>
            <div class="form-group">
              <label>Location Name</label>
              <input 
                v-model="createEntityForm.location_name" 
                type="text" 
                class="form-input"
                placeholder="e.g., Store #101006"
              />
            </div>
          </div>
          <div class="info-note">
            <i class="fas fa-lightbulb"></i>
            <span>If provided, a location will be created and linked to this entity.</span>
          </div>
        </div>
        
        <!-- Agent Assignment Section -->
        <div class="form-section">
          <h4 class="section-title">
            <i class="fas fa-user-tie"></i> Insurance Agent (Optional)
          </h4>
          <div class="form-group">
            <label>Select Agent</label>
            <select 
              v-model="createEntityForm.agent_id" 
              class="form-input"
            >
              <option value="">-- No Agent --</option>
              <option 
                v-for="agent in availableAgents" 
                :key="agent.id" 
                :value="agent.id"
              >
                {{ agent.name }}
              </option>
            </select>
          </div>
          <div class="agent-actions">
            <button 
              @click="showCreateAgentInline = !showCreateAgentInline" 
              class="btn-link"
              type="button"
            >
              <i class="fas fa-plus-circle"></i>
              {{ showCreateAgentInline ? 'Cancel' : 'Create New Agent' }}
            </button>
          </div>
          
          <!-- Inline Agent Creation -->
          <div v-if="showCreateAgentInline" class="inline-form">
            <div class="form-row">
              <div class="form-group">
                <input 
                  v-model="newAgentForm.name" 
                  type="text" 
                  class="form-input"
                  placeholder="Agent/Agency Name"
                />
              </div>
              <div class="form-group">
                <input 
                  v-model="newAgentForm.email" 
                  type="email" 
                  class="form-input"
                  placeholder="Email"
                />
              </div>
            </div>
            <button 
              @click="createAgentInline" 
              class="btn btn-sm btn-primary"
              :disabled="!newAgentForm.name.trim()"
              type="button"
            >
              <i class="fas fa-check"></i>
              Add Agent
            </button>
          </div>
        </div>
        
        <div class="form-group checkbox-group">
          <label class="checkbox-label">
            <input 
              type="checkbox" 
              v-model="createEntityForm.send_coi_request"
            />
            <span class="checkbox-text">
              <i class="fas fa-envelope"></i>
              Send initial COI request email
            </span>
          </label>
          <p class="field-hint">
            When checked, an email will be sent to the agent or entity contact requesting a certificate of insurance.
          </p>
        </div>

        <div class="info-note">
          <i class="fas fa-info-circle"></i>
          <span>A compliance subject will be created for this entity in the current project.</span>
        </div>
      </div>
      
      <div class="modal-footer">
        <button @click="cancelCreateEntity" class="btn btn-secondary">
          Cancel
        </button>
        <button 
          @click="submitCreateEntity" 
          class="btn btn-primary"
          :disabled="creatingEntity || !createEntityForm.entity_name.trim()"
        >
          <i v-if="creatingEntity" class="fas fa-spinner fa-spin"></i>
          <i v-else class="fas fa-plus"></i>
          {{ creatingEntity ? 'Creating...' : 'Create Entity' }}
        </button>
      </div>
    </div>
  </div>

  <!-- Add Store Modal -->
  <div v-if="showAddStoreModal" class="modal-overlay" @click.self="cancelAddStore">
    <div class="modal-dialog add-store-modal">
      <div class="modal-header">
        <h3><i class="fas fa-store-alt"></i> Add Store to {{ addStoreEntityName }}</h3>
        <button @click="cancelAddStore" class="btn-close">
          <i class="fas fa-times"></i>
        </button>
      </div>
      
      <div class="modal-body">
        <div class="form-section">
          <h4><i class="fas fa-map-marker-alt"></i> Store Information</h4>

          <div class="form-group">
            <label>Store / Location Number <span class="required">*</span></label>
            <input
              v-model="addStoreForm.store_number"
              type="text"
              placeholder="e.g., 1001A"
              class="form-control"
            />
            <small class="form-hint">The unique store number or ID for this location</small>
          </div>

          <div class="form-group">
            <label>Store Name <span class="form-optional">(Optional)</span></label>
            <input
              v-model="addStoreForm.location_name"
              type="text"
              placeholder="e.g., Downtown Toronto"
              class="form-control"
            />
          </div>

          <AddressFields v-model="addStoreForm.address" prefix="chd-store" />
        </div>
        
        <div class="form-section">
          <h4><i class="fas fa-user-tie"></i> Insurance Agent (Optional)</h4>
          
          <div class="form-group">
            <label>Select Agent</label>
            <select v-model="addStoreForm.agent_id" class="form-control">
              <option value="">-- No Agent --</option>
              <option v-for="agent in availableAgents" :key="agent.id" :value="agent.id">
                {{ agent.name }} ({{ agent.primary_email }})
              </option>
            </select>
          </div>
        </div>
        
        <div class="info-note">
          <i class="fas fa-info-circle"></i>
          <span>This will create a new location and compliance subject for this entity in the current project.</span>
        </div>
      </div>
      
      <div class="modal-footer">
        <button @click="cancelAddStore" class="btn btn-secondary">
          Cancel
        </button>
        <button 
          @click="submitAddStore" 
          class="btn btn-primary"
          :disabled="addingStore || !addStoreForm.store_number.trim()"
        >
          <i v-if="addingStore" class="fas fa-spinner fa-spin"></i>
          <i v-else class="fas fa-plus"></i>
          {{ addingStore ? 'Adding...' : 'Add Store' }}
        </button>
      </div>
    </div>
  </div>

  <!-- Coverage Documents Modal -->
  <div v-if="showCoverageDocsModal" class="modal-overlay" @click.self="closeCoverageDocsModal">
    <div class="modal-dialog coverage-docs-modal">
      <div class="modal-header">
        <h3>
          <i class="fas fa-file-alt"></i> 
          {{ selectedCoverageType }} Documents
          <span v-if="coverageDocuments.length > 0" class="doc-count">({{ coverageDocuments.length }})</span>
        </h3>
        <button @click="closeCoverageDocsModal" class="btn-close">
          <i class="fas fa-times"></i>
        </button>
      </div>
      
      <div class="modal-body">
        <div v-if="loadingCoverageDocs" class="loading-state">
          <i class="fas fa-spinner fa-spin"></i>
          <p>Loading documents...</p>
        </div>
        
        <div v-else-if="coverageDocuments.length === 0" class="empty-state">
          <i class="fas fa-file-excel"></i>
          <p>No documents found for {{ selectedCoverageType }}</p>
        </div>
        
        <div v-else class="documents-list">
          <div 
            v-for="doc in coverageDocuments" 
            :key="doc.id"
            class="document-item"
            :class="{ passed: doc.passed, failed: !doc.passed }"
          >
            <div class="doc-icon">
              <i :class="doc.passed ? 'fas fa-check-circle' : 'fas fa-times-circle'"></i>
            </div>
            <div class="doc-info">
              <div class="doc-name">{{ doc.name }}</div>
              <div class="doc-meta">
                <span class="doc-type">{{ doc.document_type }}</span>
                <span class="doc-date">{{ formatDate(doc.uploaded_at) }}</span>
                <span class="doc-requirement">{{ doc.requirement_name }}</span>
              </div>
              <div v-if="doc.evidence" class="doc-evidence">
                <i class="fas fa-quote-left"></i>
                {{ doc.evidence }}
              </div>
            </div>
            <div class="doc-actions">
              <button 
                @click="openDocument(doc)" 
                class="btn btn-sm btn-primary"
                :title="`Open ${doc.name}`"
              >
                <i class="fas fa-external-link-alt"></i>
                View
              </button>
            </div>
          </div>
        </div>
      </div>
      
      <div class="modal-footer">
        <button @click="closeCoverageDocsModal" class="btn btn-secondary">
          Close
        </button>
      </div>
    </div>
  </div>

  <!-- Bulk Import Modal -->
  <div v-if="showBulkImportModal" class="modal-overlay" @click.self="closeBulkImportModal">
    <div class="modal-dialog bulk-import-modal">
      <div class="modal-header">
        <h3>
          <i class="fas fa-file-import"></i> 
          Bulk Import Entities & Locations
        </h3>
        <button @click="closeBulkImportModal" class="btn-close">
          <i class="fas fa-times"></i>
        </button>
      </div>
      
      <div class="modal-body">
        <!-- Step 1: Paste Input -->
        <div v-if="bulkImportStep === 1" class="import-step">
          <div class="step-header">
            <h4>Step 1: Paste Your Data</h4>
            <p class="step-subtitle">Paste entities/stores in any format - we'll figure it out!</p>
          </div>
          
          <div class="format-examples">
            <div class="example-card">
              <div class="example-title"><i class="fas fa-list"></i> Simple List</div>
              <code>ABC Construction, 123 Main St, Austin TX 78701, Store #101
XYZ LLC, 456 Oak Ave, Dallas TX 75201, Store #102</code>
            </div>
            <div class="example-card">
              <div class="example-title"><i class="fas fa-table"></i> CSV Format</div>
              <code>Name, Address, City, State, Zip, Store
ABC Construction, 123 Main St, Austin, TX, 78701, 101</code>
            </div>
            <div class="example-card">
              <div class="example-title"><i class="fas fa-bars"></i> Pipe Delimited</div>
              <code>ABC Construction | 123 Main St | Austin | TX | 78701 | 101</code>
            </div>
          </div>
          
          <div class="form-group">
            <label>Paste your data here:</label>
            <textarea 
              v-model="bulkImportInput"
              class="bulk-input-textarea"
              placeholder="Paste entities and stores here... (any format works)"
              rows="12"
              autofocus
            ></textarea>
            <div class="input-hint">
              <i class="fas fa-lightbulb"></i>
              Tip: Include store numbers, addresses, emails, and phone numbers if available
            </div>
          </div>
        </div>
        
        <!-- Step 2: Review & Confirm -->
        <div v-if="bulkImportStep === 2" class="import-step">
          <div class="step-header">
            <h4>Step 2: Review Parsed Data</h4>
            <p class="step-subtitle">Check for duplicates and errors before creating</p>
          </div>
          
          <div class="parse-summary">
            <div class="summary-stat success">
              <i class="fas fa-check-circle"></i>
              <span>{{ bulkImportPreview.summary?.total_valid || 0 }} ready to create</span>
            </div>
            <div v-if="bulkImportPreview.summary?.total_duplicates > 0" class="summary-stat warning">
              <i class="fas fa-exclamation-triangle"></i>
              <span>{{ bulkImportPreview.summary.total_duplicates }} potential duplicates</span>
            </div>
            <div v-if="bulkImportPreview.summary?.total_errors > 0" class="summary-stat error">
              <i class="fas fa-times-circle"></i>
              <span>{{ bulkImportPreview.summary.total_errors }} errors</span>
            </div>
          </div>
          
          <!-- Agent Assignment (Optional) -->
          <div class="agent-assignment-section">
            <h5><i class="fas fa-user-tie"></i> Assign Insurance Agent (Optional)</h5>
            <p class="section-help">Select an agent to assign to all imported entities</p>
            <div class="form-group">
              <select 
                v-model="bulkImportAgentId" 
                class="form-input"
              >
                <option value="">-- No Agent (Skip) --</option>
                <option 
                  v-for="agent in availableAgents" 
                  :key="agent.id" 
                  :value="agent.id"
                >
                  {{ agent.name }}
                </option>
              </select>
            </div>
          </div>
          
          <!-- Errors -->
          <div v-if="bulkImportPreview.errors?.length" class="errors-section">
            <h5><i class="fas fa-exclamation-circle"></i> Errors ({{ bulkImportPreview.errors.length }})</h5>
            <div class="error-list">
              <div v-for="(error, idx) in bulkImportPreview.errors" :key="idx" class="error-item">
                <span class="error-row">Line {{ error.row_number }}:</span>
                <span class="error-message">{{ error.error }}</span>
                <code class="error-raw">{{ error.raw_line }}</code>
              </div>
            </div>
          </div>
          
          <!-- Duplicates -->
          <div v-if="bulkImportPreview.duplicates_found?.length" class="duplicates-section">
            <h5><i class="fas fa-clone"></i> Potential Duplicates ({{ bulkImportPreview.duplicates_found.length }})</h5>
            <div class="duplicate-list">
              <div 
                v-for="(dup, idx) in bulkImportPreview.duplicates_found" 
                :key="idx"
                class="duplicate-item"
              >
                <div class="duplicate-header">
                  <span class="duplicate-similarity">{{ Math.round(dup.similarity * 100) }}% match</span>
                  <span v-if="dup.already_tracked" class="already-tracked-badge">
                    <i class="fas fa-check"></i> Already Tracking
                  </span>
                </div>
                <div class="duplicate-comparison">
                  <div class="comparison-col">
                    <div class="col-label">Your Input:</div>
                    <div class="entity-name">{{ dup.input_row.entity_name }}</div>
                    <div class="entity-details">
                      <span v-if="dup.input_row.store_number">Store #{{ dup.input_row.store_number }}</span>
                      <span v-if="dup.input_row.city">{{ dup.input_row.city }}, {{ dup.input_row.state }}</span>
                    </div>
                  </div>
                  <div class="comparison-arrow">
                    <i class="fas fa-arrows-alt-h"></i>
                  </div>
                  <div class="comparison-col">
                    <div class="col-label">Existing Entity:</div>
                    <div class="entity-name">{{ dup.existing_entity.name }}</div>
                    <div class="entity-details">
                      <span v-if="dup.existing_entity.email">{{ dup.existing_entity.email }}</span>
                      <span v-if="dup.existing_entity.address">{{ dup.existing_entity.address }}</span>
                    </div>
                  </div>
                </div>
                <div class="duplicate-actions">
                  <button 
                    @click="setDuplicateAction(idx, 'use_existing')"
                    class="btn btn-sm"
                    :class="{ 'btn-primary': duplicateActions[`row_${dup.input_row.row_number}`] === 'use_existing' }"
                  >
                    <i class="fas fa-link"></i>
                    Use Existing
                  </button>
                  <button 
                    @click="setDuplicateAction(idx, 'create_new')"
                    class="btn btn-sm"
                    :class="{ 'btn-primary': duplicateActions[`row_${dup.input_row.row_number}`] === 'create_new' }"
                  >
                    <i class="fas fa-plus"></i>
                    Create New
                  </button>
                  <button 
                    @click="setDuplicateAction(idx, 'skip')"
                    class="btn btn-sm btn-secondary"
                  >
                    <i class="fas fa-ban"></i>
                    Skip
                  </button>
                </div>
              </div>
            </div>
          </div>
          
          <!-- Valid Rows Preview -->
          <div v-if="bulkImportPreview.valid_rows?.length" class="valid-rows-section">
            <h5><i class="fas fa-check-circle"></i> Ready to Create ({{ bulkImportPreview.valid_rows.length }})</h5>
            <div class="valid-rows-list">
              <div 
                v-for="(row, idx) in bulkImportPreview.valid_rows.slice(0, 10)" 
                :key="idx"
                class="valid-row-item"
              >
                <div class="row-icon">
                  <i class="fas fa-building"></i>
                </div>
                <div class="row-info">
                  <div class="row-name">{{ row.entity_name }}</div>
                  <div class="row-details">
                    <span v-if="row.store_number" class="detail-badge">
                      <i class="fas fa-store"></i> #{{ row.store_number }}
                    </span>
                    <span v-if="row.city" class="detail-text">
                      {{ row.city }}, {{ row.state }}
                    </span>
                    <span v-if="row.email" class="detail-text">
                      <i class="fas fa-envelope"></i> {{ row.email }}
                    </span>
                  </div>
                </div>
              </div>
              <div v-if="bulkImportPreview.valid_rows.length > 10" class="more-rows">
                +{{ bulkImportPreview.valid_rows.length - 10 }} more rows
              </div>
            </div>
          </div>
        </div>
        
        <!-- Step 3: Results -->
        <div v-if="bulkImportStep === 3" class="import-step">
          <div class="step-header">
            <h4>Import Complete!</h4>
            <p class="step-subtitle">Your entities and locations have been created</p>
          </div>
          
          <div class="import-results">
            <div class="result-stat success">
              <i class="fas fa-building"></i>
              <div class="stat-info">
                <div class="stat-value">{{ bulkImportResults.summary?.total_entities || 0 }}</div>
                <div class="stat-label">Entities Created</div>
              </div>
            </div>
            <div class="result-stat success">
              <i class="fas fa-store"></i>
              <div class="stat-info">
                <div class="stat-value">{{ bulkImportResults.summary?.total_locations || 0 }}</div>
                <div class="stat-label">Locations Created</div>
              </div>
            </div>
            <div class="result-stat success">
              <i class="fas fa-clipboard-check"></i>
              <div class="stat-info">
                <div class="stat-value">{{ bulkImportResults.summary?.total_subjects || 0 }}</div>
                <div class="stat-label">Now Tracking</div>
              </div>
            </div>
          </div>
          
          <div v-if="bulkImportResults.errors?.length" class="import-errors">
            <h5><i class="fas fa-exclamation-triangle"></i> Some items failed ({{ bulkImportResults.errors.length }})</h5>
            <div class="error-list">
              <div v-for="(error, idx) in bulkImportResults.errors" :key="idx" class="error-item">
                <span class="error-row">Line {{ error.row_number }}:</span>
                <span class="error-message">{{ error.error }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <div class="modal-footer">
        <button 
          v-if="bulkImportStep === 1"
          @click="closeBulkImportModal" 
          class="btn btn-secondary"
        >
          Cancel
        </button>
        <button 
          v-if="bulkImportStep === 1"
          @click="parseBulkInput" 
          class="btn btn-primary"
          :disabled="!bulkImportInput.trim() || parsingBulkInput"
        >
          <i v-if="parsingBulkInput" class="fas fa-spinner fa-spin"></i>
          <i v-else class="fas fa-arrow-right"></i>
          {{ parsingBulkInput ? 'Parsing...' : 'Next: Review' }}
        </button>
        
        <button 
          v-if="bulkImportStep === 2"
          @click="bulkImportStep = 1" 
          class="btn btn-secondary"
        >
          <i class="fas fa-arrow-left"></i>
          Back
        </button>
        <button 
          v-if="bulkImportStep === 2"
          @click="createBulkImport" 
          class="btn btn-primary"
          :disabled="creatingBulkImport || (!bulkImportPreview.valid_rows?.length && !hasResolvedDuplicates)"
        >
          <i v-if="creatingBulkImport" class="fas fa-spinner fa-spin"></i>
          <i v-else class="fas fa-check"></i>
          {{ creatingBulkImport ? 'Creating...' : `Create ${getTotalToCreate()} Items` }}
        </button>
        
        <button 
          v-if="bulkImportStep === 3"
          @click="finishBulkImport" 
          class="btn btn-primary"
        >
          <i class="fas fa-check"></i>
          Done
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, nextTick } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { debounce } from 'lodash-es'
import { UI_CONSTANTS } from '@/constants'
import api from '@/services/api'
import {
  fetchNotificationRecipients,
  generateNotificationDraft,
  sendComplianceNotifications,
} from '@/services/complianceNotificationService'
import ComplianceConsultChat from '@/components/compliance/ComplianceConsultChat.vue'
import ComplianceOutreachDrawer from '@/components/compliance/ComplianceOutreachDrawer.vue'
import EntityDetailPanel from '@/components/compliance/EntityDetailPanel.vue'
import EntityDocumentsModal from '@/components/compliance/EntityDocumentsModal.vue'
import AddressFields from '@/components/shared/AddressFields.vue'

export default {
  name: 'ComplianceHealthDashboard',

  props: {
    embedded: { type: Boolean, default: false },
    initialProjectId: { type: [Number, String], default: null },
  },
  
  components: {
    ComplianceConsultChat,
    ComplianceOutreachDrawer,
    EntityDetailPanel,
    EntityDocumentsModal,
    AddressFields,
  },
  
  setup(props) {
    const route = useRoute()
    const router = useRouter()
    
    // State
    const loading = ref(false)
    const loadingEntities = ref(false)
    const loadingMore = ref(false)
    const loadingDetail = ref(false)
    const calculating = ref(false)
    const generatingAI = ref(false)
    const sendingNotification = ref(false)
    
    const projects = ref([])
    const projectId = ref('')
    const summary = ref({})
    const entities = ref([])
    const selectedEntity = ref(null)
    const entityDetail = ref(null)
    
    // View state
    const consultChatRef = ref(null)
    const agentConsultEngaged = ref(true)
    const entityDirectoryOpen = ref(false)
    const pendingInitialQuery = ref('')
    const activeView = ref('detail') // 'detail', 'action-panel', or 'notification-preview'
    const showEmbeddedAgent = computed(() => (
      activeView.value === 'detail'
      && !entityDetail.value
      && agentConsultEngaged.value
    ))
    const showEntityDirectory = computed(() => (
      !showEmbeddedAgent.value
      || !!selectedEntity.value
      || entityDirectoryOpen.value
    ))
    const consultFocusLayout = computed(() => (
      showEmbeddedAgent.value
      && !selectedEntity.value
      && !entityDirectoryOpen.value
    ))
    const actionPanelTab = ref('results') // 'results' or 'draft'
    const notificationPreviewData = ref(null)
    const notificationSubcontractorFallbacks = computed(() => (
      notificationPreviewData.value?.recipientsSummary?.filter(
        recipient => recipient.needs_agent_handoff
      ).length || 0
    ))
    const notificationUnavailableRecipients = computed(() => (
      notificationPreviewData.value?.recipientsSummary?.filter(
        recipient => recipient.delivery_route === 'unavailable'
      ).length || 0
    ))
    // Resolved To addresses for the draft. Populated from the agent payload
    // when it has one, then refreshed against the backend so the operator
    // sees the real address — including entities that have none.
    const draftRecipients = ref([])
    const loadingDraftRecipients = ref(false)
    const draftRecipientCount = computed(() => (
      draftRecipients.value.length
      || notificationPreviewData.value?.entityIds?.length
      || 0
    ))
    const draftMissingEmailCount = computed(() => (
      draftRecipients.value.filter(recipient => !recipient.recipient_email).length
    ))
    const draftSendableCount = computed(() => {
      if (!draftRecipients.value.length) {
        return notificationPreviewData.value?.entityIds?.length || 0
      }
      return draftRecipients.value.filter(recipient => recipient.recipient_email).length
    })
    const queryResultsData = ref(null)
    const selectedResultEntityIds = ref([])
    
    // Phase 2: Entity expansion state
    const expandedEntityId = ref(null)
    const expandedEntityDetail = ref(null)
    const loadingEntityDetail = ref(false)
    const entityDetailError = ref(null)

    // Docs & Analysis modal state
    const showDocsModal = ref(false)
    const docsModalSubjectId = ref(null)
    const docsModalEntityName = ref('')
    
    // AI Email generation state
    const emailTone = ref('professional')
    const emailUrgency = ref('normal')
    const emailInstructions = ref('')
    const generatingEmail = ref(false)
    
    // Entity creation modal state
    const showCreateEntityModal = ref(false)
    const creatingEntity = ref(false)
    const createEntityForm = ref({
      entity_name: '',
      entity_type: 'COMPANY',
      email: '',
      phone: '',
      address: {
        line1: '',
        city: '',
        state: '',
        postal_code: ''
      },
      store_number: '',
      location_name: '',
      agent_id: '',
      send_coi_request: true
    })
    
    // Agent management state
    const availableAgents = ref([])
    const showCreateAgentInline = ref(false)
    const newAgentForm = ref({
      name: '',
      email: ''
    })
    
    // Add Store modal state
    const showAddStoreModal = ref(false)
    const addingStore = ref(false)
    const addStoreEntityId = ref(null)
    const addStoreEntityName = ref('')
    const addStoreForm = ref({
      store_number: '',
      location_name: '',
      address: {
        address_line1: '',
        address_line2: '',
        city: '',
        state: '',
        province: '',
        postal_code: '',
        country: 'US',
      },
      agent_id: ''
    })
    
    // Coverage documents modal state
    const showCoverageDocsModal = ref(false)
    const selectedCoverageType = ref('')
    const coverageDocuments = ref([])
    const loadingCoverageDocs = ref(false)
    
    // Bulk import modal state
    const showBulkImportModal = ref(false)
    const bulkImportStep = ref(1) // 1=input, 2=review, 3=results
    const bulkImportInput = ref('')
    const bulkImportPreview = ref({})
    const bulkImportResults = ref({})
    const parsingBulkInput = ref(false)
    const creatingBulkImport = ref(false)
    const duplicateActions = ref({}) // { row_1: 'use_existing', row_2: 'create_new' }
    const bulkImportAgentId = ref('') // Agent to assign to all imported entities
    
    const statusFilter = ref('')
    const cohortFilter = ref('')
    const searchQuery = ref('')
    const includeInactive = ref(true)
    const offset = ref(0)
    const limit = ref(UI_CONSTANTS.MAX_INITIAL_LOAD)
    const hasMoreEntities = ref(false)
    const entityTotal = ref(0)

    const opsBriefing = ref(null)
    const loadingOpsBriefing = ref(false)
    const showBriefingReportModal = ref(false)
    const briefingReportDryRun = ref(null)
    const briefingReportRecipients = ref('')
    const sendingBriefingReport = ref(false)

    const opsCohortCards = computed(() => [
      { key: 'non_compliant', label: 'Non-compliant', statusFilter: null },
      { key: 'no_data', label: 'No data', statusFilter: 'no_data' },
      { key: 'missing_agent', label: 'Missing agent', statusFilter: null },
      { key: 'no_contact', label: 'No contacts', statusFilter: null },
      { key: 'stale_outreach', label: 'Stale outreach', statusFilter: null },
      { key: 'waiver_review', label: 'Exceptions', statusFilter: null },
    ])

    const activeCohortLabel = computed(() => {
      if (cohortFilter.value === 'needs_agent_email') return 'Stores needing an agent email'
      const card = opsCohortCards.value.find(c => c.key === cohortFilter.value)
      return card?.label || cohortFilter.value
    })

    const outreachSummary = ref(null)
    const outreachDelivered = computed(() => {
      const states = outreachSummary.value?.email_states || {}
      return (states.delivered || 0) + (states.opened || 0)
    })
    const outreachWindowLabel = computed(() => {
      const s = outreachSummary.value
      if (!s?.since) return ''
      const since = new Date(s.since).toLocaleString(undefined, {
        month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
      })
      return s.basis === 'last_checked' ? `Since you last checked (${since})` : `Last 24 hours (since ${since})`
    })
    
    // Computed
    const selectedProject = computed(() => {
      if (!projectId.value) return null
      if (!Array.isArray(projects.value)) return null
      return projects.value.find(p => p.id == projectId.value)
    })
    
    // Methods
    const loadProjects = async () => {
      try {
        const response = await api.get('/projects/')
        // API returns { projects: [...] }
        projects.value = response.data.projects || response.data.results || response.data || []
      } catch (error) {
        console.error('Error loading projects:', error)
      }
    }
    
    const loadSummary = async () => {
      loading.value = true
      try {
        const params = projectId.value ? `?project_id=${projectId.value}` : ''
        const response = await api.get(`/compliance-health/summary/${params}`)
        summary.value = response.data
      } catch (error) {
        console.error('Error loading health summary:', error)
        if (window.$toast) {
          window.$toast.error('Failed to load compliance health summary')
        }
      } finally {
        loading.value = false
      }
    }
    
    const loadEntities = async (reset = true) => {
      if (reset) {
        loadingEntities.value = true
        offset.value = 0
        entities.value = []
      } else {
        loadingMore.value = true
      }
      
      try {
        const params = new URLSearchParams()
        params.set('limit', limit.value)
        params.set('offset', offset.value)
        params.set('sort_by', 'score')
        params.set('sort_order', 'asc')
        
        if (projectId.value) {
          params.set('project_id', projectId.value)
        }
        if (statusFilter.value) {
          params.set('status', statusFilter.value)
        }
        if (cohortFilter.value) {
          params.set('cohort', cohortFilter.value)
          params.set('stale_days', String(opsBriefing.value?.stale_days || 14))
        }
        if (searchQuery.value.trim()) {
          params.set('search', searchQuery.value.trim())
        }
        if (!includeInactive.value) {
          params.set('include_inactive', 'false')
        }
        
        const response = await api.get(`/compliance-health/entities/?${params}`)
        
        if (reset) {
          entities.value = response.data.entities || []
        } else {
          entities.value = [...entities.value, ...(response.data.entities || [])]
        }
        
        hasMoreEntities.value = response.data.has_more
        entityTotal.value = response.data.total || 0
        offset.value = response.data.offset + response.data.entities.length
        
      } catch (error) {
        console.error('Error loading entities:', error)
      } finally {
        loadingEntities.value = false
        loadingMore.value = false
      }
    }
    
    const loadOpsBriefing = async () => {
      if (!projectId.value) {
        opsBriefing.value = null
        return
      }
      loadingOpsBriefing.value = true
      try {
        const response = await api.get(`/compliance-health/ops-briefing/?project_id=${projectId.value}`)
        opsBriefing.value = response.data
      } catch (error) {
        console.error('Error loading ops briefing:', error)
        if (window.$toast) {
          window.$toast.error('Failed to load ops briefing')
        }
      } finally {
        loadingOpsBriefing.value = false
      }
      loadOutreachSummary()
    }

    const loadOutreachSummary = async () => {
      if (!projectId.value) {
        outreachSummary.value = null
        return
      }
      try {
        const response = await api.get('/compliance-health/outreach-summary/', {
          params: { project_id: projectId.value },
        })
        outreachSummary.value = response.data
      } catch (error) {
        console.error('Error loading outreach summary:', error)
        outreachSummary.value = null
      }
    }

    const onProjectChange = () => {
      // Update URL with project_id query param
      if (projectId.value) {
        router.push({ query: { project_id: projectId.value } })
      } else {
        router.push({ query: {} })
      }
      selectedEntity.value = null
      entityDetail.value = null
      cohortFilter.value = ''
      opsBriefing.value = null
      outreachSummary.value = null
      loadSummary()
      loadEntities(true)
      loadOpsBriefing()
    }
    
    const loadMoreEntities = () => {
      loadEntities(false)
    }
    
    const onIncludeInactiveChange = () => {
      loadEntities(true)
    }
    
    const selectEntity = async (entity) => {
      entityDirectoryOpen.value = true
      selectedEntity.value = entity
      loadingDetail.value = true
      
      try {
        const response = await api.get(`/compliance-health/entities/${entity.subject_id}/?include_history=true`)
        entityDetail.value = response.data
      } catch (error) {
        console.error('Error loading entity detail:', error)
        entityDetail.value = null
      } finally {
        loadingDetail.value = false
      }
    }
    
    const handleShowEntityDetails = async (subjectId) => {
      // Find the entity in the current list by subject_id
      const entity = entities.value.find(e => e.subject_id === subjectId)
      
      if (entity) {
        // Select it to show in detail panel
        await selectEntity(entity)
      } else {
        // Entity not in current list, fetch it directly
        try {
          loadingDetail.value = true
          const response = await api.get(`/compliance-health/entities/${subjectId}/?include_history=true`)
          entityDetail.value = response.data
          
          // Create a minimal entity object for selectedEntity
          selectedEntity.value = {
            subject_id: subjectId,
            entity_name: response.data.entity_name,
            overall_score: response.data.overall_score,
            status_label: response.data.status_label
          }
        } catch (error) {
          console.error('Error loading entity detail:', error)
          if (window.$toast) {
            window.$toast.error('Failed to load entity details')
          }
        } finally {
          loadingDetail.value = false
        }
      }
    }
    
    const refreshData = async () => {
      await Promise.all([
        loadSummary(),
        loadEntities(true),
        loadOpsBriefing(),
      ])
      
      // Refresh selected entity if any
      if (selectedEntity.value) {
        selectEntity(selectedEntity.value)
      }
    }
    
    const calculateHealth = async () => {
      if (!projectId.value) return
      
      calculating.value = true
      try {
        const response = await api.post('/compliance-health/calculate/', {
          project_id: projectId.value,
          skip_llm: true  // Skip LLM for faster initial calculation
        })
        
        if (response.data.mode === 'async') {
          // Async task started - poll for completion
          if (window.$toast) {
            window.$toast.info(
              `Health calculation started for ${response.data.total_subjects} entities. ` +
              `This may take a few minutes...`
            )
          }
          
          // Store task ID and start polling
          const taskId = response.data.task_id
          pollTaskStatus(taskId)
          
        } else {
          // Sync completed
          if (window.$toast) {
            window.$toast.success(`Calculated health for ${response.data.calculated} entities`)
          }
          
          // Reload data
          await Promise.all([
            loadSummary(),
            loadEntities(true)
          ])
          calculating.value = false
        }
      } catch (error) {
        console.error('Error calculating health:', error)
        if (window.$toast) {
          window.$toast.error('Failed to calculate health: ' + (error.response?.data?.error || error.message))
        }
        calculating.value = false
      }
    }
    
    const pollTaskStatus = async (taskId) => {
      // Poll every 5 seconds
      const pollInterval = setInterval(async () => {
        try {
          const response = await api.get(`/compliance-health/tasks/${taskId}/`)
          
          if (response.data.ready) {
            clearInterval(pollInterval)
            calculating.value = false
            
            if (response.data.successful) {
              if (window.$toast) {
                window.$toast.success('Health calculation completed!')
              }
              // Reload data
              await Promise.all([
                loadSummary(),
                loadEntities(true)
              ])
            } else {
              if (window.$toast) {
                window.$toast.error('Health calculation failed: ' + (response.data.error || 'Unknown error'))
              }
            }
          }
        } catch (error) {
          console.error('Error polling task status:', error)
          // Keep polling on transient errors
        }
      }, 5000)
      
      // Stop polling after 30 minutes
      setTimeout(() => {
        clearInterval(pollInterval)
        if (calculating.value) {
          calculating.value = false
          if (window.$toast) {
            window.$toast.warning('Health calculation is taking longer than expected. Check back later.')
          }
        }
      }, 30 * 60 * 1000)
    }
    
    const debouncedSearch = debounce(() => {
      loadEntities(true)
    }, UI_CONSTANTS.SEARCH_DEBOUNCE_MS)
    
    const generateAIAnalysis = async () => {
      if (!entityDetail.value?.subject_id) return
      
      generatingAI.value = true
      try {
        // Trigger health calculation with LLM for this specific entity
        const response = await api.post('/compliance-health/calculate/', {
          subject_id: entityDetail.value.subject_id,
          skip_llm: false  // Enable LLM analysis
        })
        
        if (response.data.status === 'success') {
          // Reload entity detail to get the new AI analysis
          const detailResponse = await api.get(
            `/compliance-health/entities/${entityDetail.value.subject_id}/?include_history=true`
          )
          entityDetail.value = detailResponse.data
          
          if (window.$toast) {
            window.$toast.success('AI analysis generated successfully!')
          }
        }
      } catch (error) {
        console.error('Error generating AI analysis:', error)
        if (window.$toast) {
          window.$toast.error('Failed to generate AI analysis: ' + (error.response?.data?.error || error.message))
        }
      } finally {
        generatingAI.value = false
      }
    }
    
    // Agent chat event handlers
    const handleFilterEntities = (filter) => {
      entityDirectoryOpen.value = true
      // Apply filter from agent suggestion
      if (filter.cohort) {
        cohortFilter.value = filter.cohort
      }
      if (filter.status) {
        statusFilter.value = filter.status.toLowerCase()
      }
      if (filter.search) {
        searchQuery.value = filter.search
      }
      loadEntities(true)
    }

    const applyBriefingCohort = (cohortKey, statusFilterOverride) => {
      cohortFilter.value = cohortKey
      statusFilter.value = statusFilterOverride || ''
      loadEntities(true)
    }

    const clearCohortFilter = () => {
      cohortFilter.value = ''
      statusFilter.value = ''
      loadEntities(true)
    }

    const runBriefingAction = (action) => {
      if (action.filter?.cohort) {
        applyBriefingCohort(action.filter.cohort, action.filter.status || '')
      } else if (action.filter?.status) {
        cohortFilter.value = ''
        statusFilter.value = action.filter.status
        loadEntities(true)
      }
      if (action.consult_prompt) {
        triggerAgentQuery(action.consult_prompt)
      }
    }

    const escalateStaleToInsured = async () => {
      const ids = opsBriefing.value?.cohorts?.stale_outreach?.subject_ids || []
      if (!ids.length) {
        if (window.$toast) window.$toast.info('No stale outreach subjects in this briefing')
        return
      }
      try {
        const response = await api.post('/compliance-health/agent/notify/', {
          subject_ids: ids.slice(0, 25),
          project_id: projectId.value,
          recipient_preference: 'insured',
          dry_run: true,
          template: 'gap_notification',
          message: 'Follow-up on outstanding compliance gaps.',
        })
        notificationPreviewData.value = {
          entityIds: ids.slice(0, 25),
          template: 'gap_notification',
          customMessage: 'Follow-up on outstanding compliance gaps.',
          subject: 'Compliance follow-up required',
          aiGenerated: false,
          generationTime: null,
          recipientPreference: 'insured',
          recipientsSummary: response.data.previews || response.data.recipients || null,
        }
        activeView.value = 'notification-preview'
        hydrateDraftRecipients()
      } catch (error) {
        console.error('Error preparing insured escalation:', error)
        if (window.$toast) {
          window.$toast.error(error.response?.data?.error || 'Could not prepare insured escalation')
        }
      }
    }

    const openBriefingReportModal = async (dryRun = true) => {
      if (!projectId.value) return
      showBriefingReportModal.value = true
      sendingBriefingReport.value = true
      briefingReportDryRun.value = null
      try {
        const recipients = briefingReportRecipients.value
          .split(',')
          .map(e => e.trim())
          .filter(Boolean)
        const response = await api.post('/compliance-health/ops-briefing/send-report/', {
          project_id: projectId.value,
          dry_run: dryRun,
          recipient_emails: recipients,
        })
        briefingReportDryRun.value = response.data
        if (!dryRun && window.$toast) {
          window.$toast.success(`Book report sent to ${recipients.length} recipient(s)`)
          closeBriefingReportModal()
        }
      } catch (error) {
        console.error('Error with book report:', error)
        if (window.$toast) {
          window.$toast.error(error.response?.data?.error || 'Book report failed')
        }
      } finally {
        sendingBriefingReport.value = false
      }
    }

    const sendBriefingReport = () => openBriefingReportModal(false)

    const closeBriefingReportModal = () => {
      showBriefingReportModal.value = false
      briefingReportDryRun.value = null
    }
    
    const handleSelectEntities = (entityIds) => {
      // For now, just filter to show these entities
      // In the future, this could enable multi-select for bulk actions
      console.log('Select entities:', entityIds)
      if (window.$toast) {
        window.$toast.info(`${entityIds.length} entities selected`)
      }
    }

    const handleShowDocument = (payload) => {
      // Open the entity documents modal scoped to this subject when we have one
      const subjectId = payload?.subject_id || payload?.subjectId
      if (subjectId) {
        docsModalSubjectId.value = subjectId
        docsModalEntityName.value = ''
        showDocsModal.value = true
        return
      }
      const documentId = payload?.document_id || payload?.documentId
      if (documentId) {
        // Fall back to opening the document in a new tab — covers the case
        // where the consult cited a document but we don't have an entity scope.
        window.open(`/documents/${documentId}`, '_blank')
      }
    }

    const goToFullConsult = () => {
      const route = projectId.value
        ? `/compliance-health/consult?project_id=${projectId.value}`
        : '/compliance-health/consult'
      router.push(route)
    }

    const hydrateDraftRecipients = async () => {
      const draft = notificationPreviewData.value
      const subjectIds = draft?.entityIds || []

      // Seed with whatever the agent already resolved so the To field is
      // never blank while the request is in flight. Bulk-send previews use a
      // different shape, so only keep rows we can render as an address.
      draftRecipients.value = (draft?.recipientsSummary || []).filter(
        row => row?.subject_id && 'recipient_email' in row
      )

      if (!subjectIds.length) {
        draftRecipients.value = []
        return
      }

      loadingDraftRecipients.value = true
      try {
        const { recipients } = await fetchNotificationRecipients(
          subjectIds,
          draft.recipientPreference || 'agent',
          draft.ccSubcontractor !== false
        )
        draftRecipients.value = recipients
        draft.recipientsSummary = recipients
      } catch (error) {
        console.error('Error resolving notification recipients:', error)
      } finally {
        loadingDraftRecipients.value = false
      }
    }

    const outreachPayload = ref(null)
    const handleNotify = (data) => {
      outreachPayload.value = data
    }
    
    const handleCreateEntity = (data) => {
      console.log('Handle create entity from agent:', data)
      // Pre-populate the form with data from the agent
      createEntityForm.value = {
        entity_name: data.entity_name || '',
        entity_type: data.entity_type || 'COMPANY',
        email: data.email || '',
        phone: data.phone || '',
        address: {
          line1: data.address?.line1 || '',
          city: data.address?.city || '',
          state: data.address?.state || '',
          postal_code: data.address?.postal_code || ''
        },
        store_number: data.store_number || '',
        location_name: data.location_name || '',
        agent_id: data.agent_id || '',
        send_coi_request: true
      }
      showCreateEntityModal.value = true
      loadAvailableAgents() // Load agents when modal opens
    }
    
    const submitCreateEntity = async () => {
      if (!createEntityForm.value.entity_name.trim()) {
        if (window.$toast) {
          window.$toast.error('Entity name is required')
        }
        return
      }
      
      creatingEntity.value = true
      try {
        const response = await api.post('/compliance-health/agent/create-entity/', {
          entity_name: createEntityForm.value.entity_name.trim(),
          entity_type: createEntityForm.value.entity_type,
          email: (createEntityForm.value.email || '').trim() || null,
          phone: (createEntityForm.value.phone || '').trim() || null,
          address: createEntityForm.value.address,
          store_number: (createEntityForm.value.store_number || '').trim() || null,
          location_name: (createEntityForm.value.location_name || '').trim() || null,
          agent_id: createEntityForm.value.agent_id || null,
          project_id: projectId.value,
          check_duplicates: true,
          send_coi_request: createEntityForm.value.send_coi_request
        })
        
        if (response.data.success) {
          if (window.$toast) {
            const msg = response.data.message || 'Entity created successfully'
            const locationMsg = response.data.location_created ? ' with location' : ''
            window.$toast.success(msg + locationMsg)

            // Show a separate notification about email status
            if (response.data.coi_request_sent === 'scheduled') {
              window.$toast.info(`COI request email will be sent to ${response.data.coi_email_to}`)
            } else if (response.data.coi_email_reason === 'no_contact_email' && createEntityForm.value.send_coi_request) {
              window.$toast.warning('No contact email found — COI request email was not sent. Add an email to the entity or agent to enable notifications.')
            }
          }
          showCreateEntityModal.value = false
          // Refresh the entities list
          await loadEntities()
          // Clear the form
          createEntityForm.value = {
            entity_name: '',
            entity_type: 'COMPANY',
            email: '',
            phone: '',
            address: { line1: '', city: '', state: '', postal_code: '' },
            store_number: '',
            location_name: '',
            agent_id: '',
            send_coi_request: true
          }
          showCreateAgentInline.value = false
        }
      } catch (error) {
        console.error('Error creating entity:', error)
        if (window.$toast) {
          window.$toast.error(error.response?.data?.error || 'Failed to create entity')
        }
      } finally {
        creatingEntity.value = false
      }
    }
    
    const loadAvailableAgents = async () => {
      try {
        // Load entities that are marked as agents (entity_type = 'AGENCY')
        const response = await api.get('/entities/', {
          params: {
            entity_type: 'AGENCY',
            limit: 100
          }
        })
        availableAgents.value = response.data.results || response.data || []
      } catch (error) {
        console.error('Error loading agents:', error)
      }
    }
    
    const createAgentInline = async () => {
      if (!newAgentForm.value.name.trim()) {
        if (window.$toast) {
          window.$toast.error('Agent name is required')
        }
        return
      }
      
      try {
        const response = await api.post('/entities/', {
          name: newAgentForm.value.name.trim(),
          entity_type: 'AGENCY',
          primary_email: newAgentForm.value.email.trim() || null
        })
        
        const newAgent = response.data
        availableAgents.value.push(newAgent)
        createEntityForm.value.agent_id = newAgent.id
        
        if (window.$toast) {
          window.$toast.success(`Agent "${newAgent.name}" created`)
        }
        
        // Clear form and hide
        newAgentForm.value = { name: '', email: '' }
        showCreateAgentInline.value = false
      } catch (error) {
        console.error('Error creating agent:', error)
        if (window.$toast) {
          window.$toast.error(error.response?.data?.error || 'Failed to create agent')
        }
      }
    }
    
    const openCreateEntityModal = () => {
      showCreateEntityModal.value = true
      loadAvailableAgents() // Load agents when modal opens
    }
    
    const cancelCreateEntity = () => {
      showCreateEntityModal.value = false
      showCreateAgentInline.value = false
      createEntityForm.value = {
        entity_name: '',
        entity_type: 'COMPANY',
        email: '',
        phone: '',
        address: { line1: '', city: '', state: '', postal_code: '' },
        store_number: '',
        location_name: '',
        agent_id: '',
        send_coi_request: true
      }
      newAgentForm.value = { name: '', email: '' }
    }
    
    // Coverage documents methods
    const viewCoverageDocuments = async (coverageType) => {
      if (!selectedEntity.value) return
      
      selectedCoverageType.value = coverageType
      showCoverageDocsModal.value = true
      loadingCoverageDocs.value = true
      coverageDocuments.value = []
      
      try {
        const subjectId = selectedEntity.value.subject_id || selectedEntity.value
        const response = await api.get(
          `/compliance-health/entities/${subjectId}/coverage-documents/`,
          {
            params: { coverage_type: coverageType }
          }
        )
        
        coverageDocuments.value = response.data.documents || []
      } catch (error) {
        console.error('Error loading coverage documents:', error)
        if (window.$toast) {
          window.$toast.error('Failed to load documents')
        }
      } finally {
        loadingCoverageDocs.value = false
      }
    }
    
    const closeCoverageDocsModal = () => {
      showCoverageDocsModal.value = false
      selectedCoverageType.value = ''
      coverageDocuments.value = []
    }
    
    const openDocument = (doc) => {
      const url = doc.file_url || doc.file
      if (url) {
        window.open(url, '_blank')
      } else {
        if (window.$toast) {
          window.$toast.error('Document URL not available')
        }
      }
    }
    
    // Bulk Import Methods
    const openBulkImportModal = () => {
      if (!projectId.value) {
        if (window.$toast) {
          window.$toast.warning('Please select a project first')
        }
        return
      }
      
      showBulkImportModal.value = true
      bulkImportStep.value = 1
      bulkImportInput.value = ''
      bulkImportPreview.value = {}
      bulkImportResults.value = {}
      duplicateActions.value = {}
      bulkImportAgentId.value = ''
      
      // Load agents for assignment
      loadAvailableAgents()
    }
    
    const closeBulkImportModal = () => {
      showBulkImportModal.value = false
      bulkImportStep.value = 1
      bulkImportInput.value = ''
      bulkImportPreview.value = {}
      bulkImportResults.value = {}
      duplicateActions.value = {}
    }
    
    const parseBulkInput = async () => {
      if (!bulkImportInput.value.trim()) {
        return
      }
      
      parsingBulkInput.value = true
      
      try {
        const response = await api.post('/compliance-health/bulk-import/parse/', {
          text_input: bulkImportInput.value,
          project_id: projectId.value
        })
        
        bulkImportPreview.value = response.data
        
        // Auto-select "use_existing" for high-confidence duplicates
        if (response.data.duplicates_found) {
          response.data.duplicates_found.forEach(dup => {
            const rowKey = `row_${dup.input_row.row_number}`
            if (dup.similarity > 0.95 || dup.already_tracked) {
              duplicateActions.value[rowKey] = 'use_existing'
            }
          })
        }
        
        bulkImportStep.value = 2
        
        if (window.$toast) {
          const total = response.data.summary?.total_parsed || 0
          const valid = response.data.summary?.total_valid || 0
          window.$toast.success(`Parsed ${total} rows, ${valid} ready to create`)
        }
      } catch (error) {
        console.error('Error parsing bulk input:', error)
        if (window.$toast) {
          window.$toast.error('Failed to parse input: ' + (error.response?.data?.error || error.message))
        }
      } finally {
        parsingBulkInput.value = false
      }
    }
    
    const setDuplicateAction = (dupIndex, action) => {
      const dup = bulkImportPreview.value.duplicates_found[dupIndex]
      const rowKey = `row_${dup.input_row.row_number}`
      
      if (action === 'skip') {
        delete duplicateActions.value[rowKey]
      } else {
        duplicateActions.value[rowKey] = action
        
        // If using existing, store the entity ID
        if (action === 'use_existing') {
          dup.input_row.existing_entity_id = dup.existing_entity.id
        }
      }
    }
    
    const getTotalToCreate = () => {
      const validCount = bulkImportPreview.value.valid_rows?.length || 0
      const resolvedDuplicates = Object.values(duplicateActions.value).filter(
        action => action === 'create_new' || action === 'use_existing'
      ).length
      
      return validCount + resolvedDuplicates
    }
    
    const hasResolvedDuplicates = computed(() => {
      const duplicatesCount = bulkImportPreview.value.duplicates_found?.length || 0
      const resolvedCount = Object.keys(duplicateActions.value).length
      return duplicatesCount > 0 && resolvedCount === duplicatesCount
    })
    
    const createBulkImport = async () => {
      creatingBulkImport.value = true
      
      try {
        // Combine valid rows with resolved duplicates
        const rowsToCreate = [
          ...(bulkImportPreview.value.valid_rows || [])
        ]
        
        // Add duplicates that user chose to create new or use existing
        if (bulkImportPreview.value.duplicates_found) {
          bulkImportPreview.value.duplicates_found.forEach(dup => {
            const rowKey = `row_${dup.input_row.row_number}`
            const action = duplicateActions.value[rowKey]
            
            if (action === 'create_new' || action === 'use_existing') {
              rowsToCreate.push(dup.input_row)
            }
          })
        }
        
        const response = await api.post('/compliance-health/bulk-import/create/', {
          rows: rowsToCreate,
          project_id: projectId.value,
          create_locations: true,
          duplicate_actions: duplicateActions.value,
          agent_id: bulkImportAgentId.value || null
        })
        
        bulkImportResults.value = response.data
        bulkImportStep.value = 3
        
        if (window.$toast) {
          const summary = response.data.summary
          window.$toast.success(
            `Created ${summary.total_entities} entities, ${summary.total_locations} locations`
          )
        }
      } catch (error) {
        console.error('Error creating bulk import:', error)
        if (window.$toast) {
          window.$toast.error('Failed to create entities: ' + (error.response?.data?.error || error.message))
        }
      } finally {
        creatingBulkImport.value = false
      }
    }
    
    const finishBulkImport = () => {
      closeBulkImportModal()
      // Refresh the entities list
      loadEntities()
      loadSummary()
    }
    
    const handleQueryResults = (data) => {
      // Embedded consult (welcome → ComplianceConsultChat): results stay on the
      // answer via ComplianceResultPreview. Do not hijack the center panel with
      // the legacy action-panel workspace.
      if (agentConsultEngaged.value) {
        return
      }

      console.log('Handle query results from agent:', data)

      // Normalize entities to ensure consistent structure with all enhanced fields
      const normalizedEntities = (data.entities || []).map(e => ({
        entity_id: e.entity_id || e.id,
        subject_id: e.subject_id || e.entity_id || e.id,
        entity_name: e.entity_name || e.name || '',
        name: e.name || e.entity_name || '',
        status: e.status || e.overall_status || 'Unknown',
        overall_status: e.overall_status || e.status || 'Unknown',
        current_status: e.current_status || null,
        is_inactive: e.is_inactive || e.current_status === 'INACTIVE',
        failing_requirements: e.failing_requirements || (e.issue ? [{ name: e.issue }] : []),
        score: e.score || e.overall_score,
        trend: e.trend || null,
        coverage_summary: e.coverage_summary || null,
        total_requirements: e.total_requirements || null,
        passing_requirements: e.passing_requirements || null
      }))
      
      queryResultsData.value = {
        title: data.title || 'Query Results',
        subtitle: data.subtitle || `Found ${normalizedEntities.length} entities`,
        coverageType: data.coverageType || data.coverage_type || data.coverage_name,
        queryConfidence: data.queryConfidence || data.confidence,
        entities: normalizedEntities,
        message: data.message,
        originalQuery: data.query
      }
      selectedResultEntityIds.value = []
      actionPanelTab.value = 'results'
      activeView.value = 'action-panel'
    }
    
    const closeActionPanel = () => {
      activeView.value = 'detail'
      // Keep data for potential reuse
    }
    
    const clearQueryResults = () => {
      activeView.value = 'detail'
      queryResultsData.value = null
      selectedResultEntityIds.value = []
    }
    
    const selectAllResults = () => {
      if (queryResultsData.value?.entities) {
        selectedResultEntityIds.value = queryResultsData.value.entities.map(e => e.subject_id || e.entity_id)
      }
    }
    
    const clearResultSelection = () => {
      selectedResultEntityIds.value = []
    }
    
    const toggleEntitySelection = (entityId) => {
      const idx = selectedResultEntityIds.value.indexOf(entityId)
      if (idx === -1) {
        selectedResultEntityIds.value.push(entityId)
      } else {
        selectedResultEntityIds.value.splice(idx, 1)
      }
    }
    
    // Phase 2: Toggle entity expansion and fetch details
    const toggleEntityExpansion = async (subjectId, event) => {
      // Prevent triggering if clicking on checkbox
      if (event?.target?.type === 'checkbox') return
      
      // If already expanded, collapse
      if (expandedEntityId.value === subjectId) {
        expandedEntityId.value = null
        expandedEntityDetail.value = null
        entityDetailError.value = null
        return
      }
      
      // Expand and fetch details
      expandedEntityId.value = subjectId
      expandedEntityDetail.value = null
      entityDetailError.value = null
      loadingEntityDetail.value = true
      
      try {
        const response = await api.get(`/compliance-health/entities/${subjectId}/`)
        expandedEntityDetail.value = response.data
      } catch (error) {
        console.error('Error fetching entity detail:', error)
        entityDetailError.value = error.response?.data?.error || 'Failed to load entity details'
      } finally {
        loadingEntityDetail.value = false
      }
    }
    
    const retryEntityDetail = () => {
      if (expandedEntityId.value) {
        toggleEntityExpansion(expandedEntityId.value)
      }
    }
    
    const handleEntityArchived = (subjectId) => {
      console.log('Entity archived:', subjectId)
      // Close the detail panel
      expandedEntityId.value = null
      expandedEntityDetail.value = null
      // Refresh the entities list to update status
      loadEntities()
      loadSummary()
    }

    const openDocsModal = (subjectId, entityName) => {
      docsModalSubjectId.value = subjectId
      docsModalEntityName.value = entityName || 'Entity'
      showDocsModal.value = true
    }

    const openEntityRecord = (subjectId, entityProjectId) => {
      const pid = entityProjectId || projectId.value
      if (!pid || !subjectId) return
      router.push({
        name: 'EntityRecord',
        params: { projectId: pid, subjectId },
        query: { tab: 'overview' },
      })
    }
    
    const handleAddStore = async (entityId) => {
      console.log('Add store to entity:', entityId)
      addStoreEntityId.value = entityId
      addStoreEntityName.value = expandedEntityDetail.value?.entity_name || 'Entity'
      
      // Load agents if not already loaded
      if (availableAgents.value.length === 0) {
        await loadAvailableAgents()
      }
      
      showAddStoreModal.value = true
    }
    
    const cancelAddStore = () => {
      showAddStoreModal.value = false
      addStoreForm.value = {
        store_number: '',
        location_name: '',
        address: {
          address_line1: '',
          address_line2: '',
          city: '',
          state: '',
          province: '',
          postal_code: '',
          country: 'US',
        },
        agent_id: ''
      }
    }
    
    const submitAddStore = async () => {
      if (!addStoreForm.value.store_number.trim()) {
        if (window.$toast) {
          window.$toast.error('Store number is required')
        }
        return
      }
      
      addingStore.value = true
      
      try {
        const addr = addStoreForm.value.address
        const response = await api.post('/compliance-health/entities/add-store/', {
          entity_id: addStoreEntityId.value,
          project_id: projectId.value,
          store_number: addStoreForm.value.store_number.trim(),
          location_name: addStoreForm.value.location_name.trim() || null,
          address_line1: addr.address_line1?.trim() || null,
          address_line2: addr.address_line2?.trim() || null,
          city: addr.city?.trim() || null,
          state: addr.state?.trim() || null,
          province: addr.province?.trim() || null,
          postal_code: addr.postal_code?.trim() || null,
          country: addr.country || null,
          agent_id: addStoreForm.value.agent_id || null
        })
        
        if (response.data.success) {
          if (window.$toast) {
            window.$toast.success(response.data.message || 'Store added successfully')
          }
          cancelAddStore()
          // Refresh the entity detail to show new store
          if (expandedEntityId.value) {
            toggleEntityExpansion(expandedEntityId.value)
          }
          // Refresh the entities list
          loadEntities()
          loadSummary()
        }
      } catch (error) {
        console.error('Error adding store:', error)
        if (window.$toast) {
          window.$toast.error('Failed to add store: ' + (error.response?.data?.error || error.message))
        }
      } finally {
        addingStore.value = false
      }
    }
    
    const startDraftFromResults = () => {
      const entityIds = selectedResultEntityIds.value.length > 0 
        ? [...selectedResultEntityIds.value]
        : (queryResultsData.value?.entities?.map(e => e.subject_id || e.entity_id) || [])
      
      if (entityIds.length === 0) {
        if (window.$toast) {
          window.$toast.warning('No entities selected')
        }
        return
      }
      
      const coverageType = queryResultsData.value?.coverageType || ''
      const template = coverageType ? `coverage_gap_${coverageType.toLowerCase().replace(/[^a-z]/g, '_')}` : 'compliance_reminder'
      
      notificationPreviewData.value = {
        entityIds,
        template,
        customMessage: '',
        subject: coverageType 
          ? `Action Required: ${coverageType} Coverage Compliance` 
          : 'Compliance Update Required',
        aiGenerated: false,
        generationTime: null,
        recipientsSummary: [],
        recipientPreference: 'agent',
      }
      actionPanelTab.value = 'draft'
      hydrateDraftRecipients()
    }
    
    const draftNotificationFromResults = () => {
      startDraftFromResults()
    }
    
    const downloadResultsAsCsv = () => {
      if (!queryResultsData.value?.entities?.length) {
        if (window.$toast) {
          window.$toast.warning('No results to download')
        }
        return
      }
      
      const entities = queryResultsData.value.entities
      
      // Build CSV headers
      const headers = [
        'Entity Name',
        'Entity ID',
        'Subject ID',
        'Status',
        'Score',
        'Trend',
        'Total Requirements',
        'Passing Requirements',
        'Failing Issues'
      ]
      
      // Build CSV rows
      const rows = entities.map(e => {
        const failingIssues = (e.failing_requirements || [])
          .map(r => formatIssueLabel(r))
          .join('; ')
        
        return [
          `"${(e.entity_name || e.name || '').replace(/"/g, '""')}"`,
          e.entity_id || '',
          e.subject_id || '',
          e.status || e.overall_status || '',
          e.score || '',
          e.trend || '',
          e.total_requirements || '',
          e.passing_requirements || '',
          `"${failingIssues.replace(/"/g, '""')}"`
        ].join(',')
      })
      
      // Combine into CSV
      const csv = [headers.join(','), ...rows].join('\n')
      
      // Create download
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      
      // Generate filename with date and query context
      const date = new Date().toISOString().split('T')[0]
      const context = queryResultsData.value.coverageType || 'query_results'
      link.download = `compliance_${context.toLowerCase().replace(/[^a-z0-9]/g, '_')}_${date}.csv`
      
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)
      
      if (window.$toast) {
        window.$toast.success(`Downloaded ${entities.length} entities to CSV`)
      }
    }
    
    const getResultStatusClass = (entity) => {
      const status = (entity.status || entity.overall_status || '').toLowerCase()
      if (status.includes('critical') || status === 'failed') return 'status-critical'
      if (status.includes('attention') || status === 'warning') return 'status-warning'
      if (status.includes('excellent') || status === 'compliant') return 'status-good'
      return 'status-unknown'
    }
    
    // Enhanced display helpers for Phase 1
    const formatEntityStatus = (status) => {
      if (!status) return null
      const map = {
        'EXCELLENT': 'Excellent',
        'GOOD': 'Good',
        'NEEDS_ATTENTION': 'Attention',
        'AT_RISK': 'At Risk',
        'CRITICAL': 'Critical',
        'failed': 'Failed',
        'passed': 'Passed'
      }
      return map[status.toUpperCase()] || status.replace(/_/g, ' ')
    }
    
    const getCoverageSummaryClass = (summary) => {
      if (!summary) return ''
      const ratio = summary.compliant / summary.total
      if (ratio >= 0.9) return 'coverage-excellent'
      if (ratio >= 0.7) return 'coverage-good'
      if (ratio >= 0.5) return 'coverage-warning'
      return 'coverage-critical'
    }
    
    const getCoverageChipClass = (coverage) => {
      if (!coverage) return ''
      if (coverage.score >= 90) return 'chip-excellent'
      if (coverage.score >= 75) return 'chip-good'
      if (coverage.score >= 50) return 'chip-warning'
      return 'chip-critical'
    }
    
    const formatIssueLabel = (req) => {
      if (!req) return ''
      if (typeof req === 'string') return req
      return (
        req.title
        || req.name
        || req.description
        || req.type
        || ''
      ).toString()
    }

    const getIssueSeverityClass = (req) => {
      if (!req || typeof req === 'string') return ''
      const severity = req.severity || req.priority || 50
      if (severity >= 90 || req.type?.includes('CRITICAL')) return 'issue-critical'
      if (severity >= 70 || req.type?.includes('EXPIRED')) return 'issue-high'
      if (severity >= 50) return 'issue-medium'
      return 'issue-low'
    }
    
    const generateAIEmail = async () => {
      if (!notificationPreviewData.value?.entityIds?.length) {
        if (window.$toast) {
          window.$toast.error('No entities selected for notification')
        }
        return
      }
      
      generatingEmail.value = true
      try {
        const generated = await generateNotificationDraft({
          entityIds: notificationPreviewData.value.entityIds,
          tone: emailTone.value,
          urgency: emailUrgency.value,
          instructions: emailInstructions.value,
        })

        notificationPreviewData.value.subject = generated.subject
        notificationPreviewData.value.customMessage = generated.body_text
        notificationPreviewData.value.aiGenerated = true
        notificationPreviewData.value.generationTime = generated.generation_time_ms

        if (window.$toast) {
          window.$toast.success('Email generated successfully!')
        }
      } catch (error) {
        console.error('Error generating AI email:', error)
        if (window.$toast) {
          window.$toast.error('Failed to generate email: ' + (error.response?.data?.error || error.message))
        } else {
          alert('Failed to generate email: ' + (error.response?.data?.error || error.message))
        }
      } finally {
        generatingEmail.value = false
      }
    }

    const getEntityName = (id) => {
      const entity = entities.value.find(e => e.subject_id === id)
      return entity ? entity.entity_name : `Entity #${id}`
    }

    const confirmNotification = async () => {
      if (!notificationPreviewData.value) return
      
      sendingNotification.value = true
      try {
        const result = await sendComplianceNotifications(
          notificationPreviewData.value,
          projectId.value
        )
        
        const emails = result.emails_sent ?? result.queued ?? 0
        const stores = result.stores_notified ?? 0
        const sentMessage = `Sent ${emails} ${emails === 1 ? 'email' : 'emails'} covering `
          + `${stores} ${stores === 1 ? 'store' : 'stores'}`
        if (window.$toast) {
          const skipped = result.skipped_no_email_count || 0
          const suppressed = result.suppressed_count || 0
          const parts = [sentMessage]
          if (skipped) parts.push(`skipped ${skipped} with no agent or subcontractor email`)
          if (suppressed) parts.push(`skipped ${suppressed} address(es) that bounced or opted out`)
          if (parts.length > 1) {
            window.$toast.warning(`${parts.join('; ')}.`)
          } else {
            window.$toast.success(`${sentMessage}.`)
          }
        } else {
          alert(`${sentMessage}.`)
        }
        
        activeView.value = 'detail'
        notificationPreviewData.value = null
        draftRecipients.value = []
      } catch (error) {
        console.error('Error sending notification:', error)
        if (window.$toast) {
          window.$toast.error('Failed to send notification: ' + (error.response?.data?.error || error.message))
        } else {
          alert('Failed to send notification: ' + (error.response?.data?.error || error.message))
        }
      } finally {
        sendingNotification.value = false
      }
    }
    
    // Formatting helpers
    const formatStatus = (status) => {
      const map = {
        'EXCELLENT': 'Excellent',
        'GOOD': 'Good',
        'NEEDS_ATTENTION': 'Needs Attention',
        'AT_RISK': 'At Risk',
        'CRITICAL': 'Critical'
      }
      return map[status] || status
    }
    
    const formatCauseType = (type) => {
      const map = {
        'NO_AGENT': 'No Agent',
        'NO_POLICY': 'Missing Policy',
        'POLICY_EXPIRED': 'Expired Policy',
        'POLICY_EXPIRING_SOON': 'Expiring Soon',
        'COVERAGE_GAP': 'Coverage Gap',
        'LIMIT_INSUFFICIENT': 'Insufficient Limits',
        'ENDORSEMENT_MISSING': 'Missing Endorsement',
        'CERTIFICATE_OUTDATED': 'Outdated Certificate'
      }
      return map[type] || type.replace(/_/g, ' ')
    }
    
    const formatDate = (dateStr) => {
      if (!dateStr) return ''
      const date = new Date(dateStr)
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    }
    
    // Styling helpers
    const getScoreClass = (score) => {
      if (score >= 90) return 'excellent'
      if (score >= 75) return 'good'
      if (score >= 50) return 'warning'
      if (score >= 25) return 'at-risk'
      return 'critical'
    }
    
    const getStatusClass = (status) => {
      const map = {
        'EXCELLENT': 'excellent',
        'GOOD': 'good',
        'NEEDS_ATTENTION': 'warning',
        'AT_RISK': 'at-risk',
        'CRITICAL': 'critical',
        'COMPLIANT': 'excellent',
        'PARTIAL': 'warning',
        'MISSING': 'critical'
      }
      return map[status] || ''
    }
    
    const getTrendClass = (trend) => {
      const map = {
        'IMPROVING': 'improving',
        'STABLE': 'stable',
        'DECLINING': 'declining'
      }
      return map[trend] || ''
    }
    
    const getTrendIcon = (trend) => {
      const map = {
        'IMPROVING': 'fas fa-arrow-up',
        'STABLE': 'fas fa-minus',
        'DECLINING': 'fas fa-arrow-down'
      }
      return map[trend] || 'fas fa-minus'
    }
    
    const getCausePriorityClass = (priority) => {
      if (priority >= 90) return 'critical'
      if (priority >= 70) return 'high'
      if (priority >= 50) return 'medium'
      return 'low'
    }
    
    const getScoreRingStyle = (score) => {
      const percentage = score || 0
      return {
        background: `conic-gradient(
          ${getScoreColor(score)} ${percentage * 3.6}deg,
          #e2e8f0 ${percentage * 3.6}deg
        )`
      }
    }
    
    const getScoreColor = (score) => {
      if (score >= 90) return '#10b981'
      if (score >= 75) return '#3b82f6'
      if (score >= 50) return '#f59e0b'
      if (score >= 25) return '#f97316'
      return '#ef4444'
    }
    
    const clearSelectedEntity = () => {
      selectedEntity.value = null
      entityDetail.value = null
      activeView.value = 'detail'
    }

    const triggerAgentQuery = (query) => {
      const text = (query || '').trim()
      if (!text) return
      clearSelectedEntity()

      if (agentConsultEngaged.value && consultChatRef.value) {
        nextTick(() => {
          if (consultChatRef.value?.sendExample) {
            consultChatRef.value.sendExample(text)
            return
          }
          if (consultChatRef.value?.sendQuery) {
            consultChatRef.value.sendQuery(text)
          }
        })
        return
      }

      pendingInitialQuery.value = text
      agentConsultEngaged.value = true
    }

    // Lifecycle
    onMounted(async () => {
      // Load projects first
      await loadProjects()
      
      // Check for project_id in query params or embedded prop
      if (props.initialProjectId) {
        projectId.value = String(props.initialProjectId)
      } else if (route.query.project_id) {
        projectId.value = route.query.project_id
      }
      
      // Load data
      await Promise.all([
        loadSummary(),
        loadEntities(true)
      ])
      if (projectId.value) {
        await loadOpsBriefing()
      }

      const initialQ = typeof route.query.q === 'string' ? route.query.q.trim() : ''
      if (initialQ) {
        await nextTick()
        triggerAgentQuery(initialQ)
      }
    })
    
    return {
      loading,
      loadingEntities,
      loadingMore,
      loadingDetail,
      calculating,
      generatingAI,
      sendingNotification,
      projects,
      projectId,
      selectedProject,
      summary,
      entities,
      selectedEntity,
      entityDetail,
      activeView,
      showEmbeddedAgent,
      showEntityDirectory,
      entityDirectoryOpen,
      consultFocusLayout,
      pendingInitialQuery,
      consultChatRef,
      clearSelectedEntity,
      notificationPreviewData,
      notificationSubcontractorFallbacks,
      notificationUnavailableRecipients,
      draftRecipients,
      loadingDraftRecipients,
      draftRecipientCount,
      draftMissingEmailCount,
      draftSendableCount,
      statusFilter,
      cohortFilter,
      opsBriefing,
      outreachSummary,
      outreachDelivered,
      outreachWindowLabel,
      loadingOpsBriefing,
      opsCohortCards,
      activeCohortLabel,
      showBriefingReportModal,
      briefingReportDryRun,
      briefingReportRecipients,
      sendingBriefingReport,
      loadOpsBriefing,
      applyBriefingCohort,
      clearCohortFilter,
      runBriefingAction,
      escalateStaleToInsured,
      openBriefingReportModal,
      sendBriefingReport,
      closeBriefingReportModal,
      searchQuery,
      includeInactive,
      onIncludeInactiveChange,
      hasMoreEntities,
      entityTotal,
      emailTone,
      emailUrgency,
      emailInstructions,
      generatingEmail,
      onProjectChange,
      loadEntities,
      loadMoreEntities,
      selectEntity,
      refreshData,
      calculateHealth,
      generateAIAnalysis,
      generateAIEmail,
      handleFilterEntities,
      handleSelectEntities,
      debouncedSearch,
      confirmNotification,
      handleNotify,
      outreachPayload,
      handleQueryResults,
      handleShowEntityDetails,
      handleShowDocument,
      goToFullConsult,
      actionPanelTab,
      queryResultsData,
      selectedResultEntityIds,
      closeActionPanel,
      clearQueryResults,
      selectAllResults,
      clearResultSelection,
      toggleEntitySelection,
      startDraftFromResults,
      draftNotificationFromResults,
      downloadResultsAsCsv,
      getResultStatusClass,
      getEntityName,
      formatStatus,
      formatCauseType,
      formatDate,
      getScoreClass,
      getStatusClass,
      getTrendClass,
      getTrendIcon,
      getCausePriorityClass,
      getScoreRingStyle,
      triggerAgentQuery,
      // Phase 1 enhanced display helpers
      formatEntityStatus,
      getCoverageSummaryClass,
      getCoverageChipClass,
      formatIssueLabel,
      getIssueSeverityClass,
      // Phase 2: Entity expansion
      expandedEntityId,
      expandedEntityDetail,
      loadingEntityDetail,
      entityDetailError,
      toggleEntityExpansion,
      retryEntityDetail,
      handleEntityArchived,
      // Docs & Analysis modal
      showDocsModal,
      docsModalSubjectId,
      docsModalEntityName,
      openDocsModal,
      openEntityRecord,
      // Entity creation modal
      showCreateEntityModal,
      creatingEntity,
      createEntityForm,
      availableAgents,
      showCreateAgentInline,
      newAgentForm,
      handleCreateEntity,
      openCreateEntityModal,
      submitCreateEntity,
      cancelCreateEntity,
      loadAvailableAgents,
      createAgentInline,
      // Add Store modal
      showAddStoreModal,
      addingStore,
      addStoreEntityName,
      addStoreForm,
      handleAddStore,
      cancelAddStore,
      submitAddStore,
      // Coverage documents modal
      showCoverageDocsModal,
      selectedCoverageType,
      coverageDocuments,
      loadingCoverageDocs,
      viewCoverageDocuments,
      closeCoverageDocsModal,
      openDocument,
      // Bulk import modal
      showBulkImportModal,
      bulkImportStep,
      bulkImportInput,
      bulkImportPreview,
      bulkImportResults,
      parsingBulkInput,
      creatingBulkImport,
      duplicateActions,
      bulkImportAgentId,
      openBulkImportModal,
      closeBulkImportModal,
      parseBulkInput,
      setDuplicateAction,
      getTotalToCreate,
      hasResolvedDuplicates,
      createBulkImport,
      finishBulkImport
    }
  }
}
</script>

<style scoped>
.compliance-health-dashboard {
  min-height: 100vh;
  background: linear-gradient(135deg, #1e3a5f 0%, #0f172a 100%);
  padding: 1rem 1.5rem;
}

/* Compact Header */
.dashboard-header {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  border-radius: 12px;
  padding: 0.75rem 1.25rem;
  margin-bottom: 0.75rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.header-title h1 {
  font-size: 1.25rem;
  font-weight: 700;
  color: #1e293b;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.header-title h1 i {
  color: #ef4444;
  font-size: 1rem;
}

.header-subtitle {
  color: #64748b;
  margin: 0;
  font-size: 0.8rem;
  margin-left: 0.5rem;
  padding-left: 0.5rem;
  border-left: 1px solid #e2e8f0;
}

.header-title {
  display: flex;
  align-items: center;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.project-select {
  padding: 0.5rem 0.75rem;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 0.85rem;
  background: white;
  min-width: 220px;
  cursor: pointer;
  font-weight: 500;
  color: #1e293b;
}

.include-inactive-toggle {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.5rem 0.75rem;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 0.8rem;
  background: white;
  color: #475569;
  cursor: pointer;
  white-space: nowrap;
  user-select: none;
}

.include-inactive-toggle input {
  cursor: pointer;
}

.inactive-badge {
  padding: 0.125rem 0.5rem;
  border-radius: 4px;
  font-size: 0.65rem;
  font-weight: 600;
  text-transform: uppercase;
  background: #f1f5f9;
  color: #64748b;
  border: 1px dashed #94a3b8;
}

.project-select:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.1);
}

.btn-refresh {
  background: #3b82f6;
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.85rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.375rem;
  transition: all 0.2s;
}

.btn-refresh:hover:not(:disabled) {
  background: #2563eb;
}

.btn-refresh:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* No Data Warning - Compact */
.no-data-warning {
  background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
  border-radius: 10px;
  padding: 0.75rem 1rem;
  margin-bottom: 0.75rem;
  border: 1px solid #f59e0b;
}

.warning-content {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.warning-content > i {
  font-size: 1.5rem;
  color: #d97706;
}

.warning-text {
  flex: 1;
}

.warning-text h3 {
  margin: 0 0 0.125rem 0;
  color: #92400e;
  font-size: 0.9rem;
}

.warning-text p {
  margin: 0;
  color: #a16207;
  font-size: 0.8rem;
}

.btn-calculate {
  background: #f59e0b;
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.8rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.375rem;
  transition: all 0.2s;
  white-space: nowrap;
}

.btn-calculate:hover:not(:disabled) {
  background: #d97706;
}

.btn-calculate:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Compact KPI Bar */
.kpi-bar {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  border-radius: 12px;
  padding: 0.75rem 1.25rem;
  margin-bottom: 1rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  flex-wrap: nowrap;
  overflow-x: auto;
}

.kpi-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.25rem 0.75rem;
  flex-shrink: 0;
}

.kpi-item.score-kpi {
  padding: 0.5rem 1rem;
  border-radius: 8px;
  background: #f8fafc;
}

.kpi-item.score-kpi.excellent { background: #d1fae5; }
.kpi-item.score-kpi.good { background: #dbeafe; }
.kpi-item.score-kpi.warning { background: #fef3c7; }
.kpi-item.score-kpi.at-risk { background: #ffedd5; }
.kpi-item.score-kpi.critical { background: #fee2e2; }

.kpi-divider {
  width: 1px;
  height: 32px;
  background: #e2e8f0;
  flex-shrink: 0;
}

.kpi-icon {
  width: 28px;
  height: 28px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.875rem;
  flex-shrink: 0;
}

.kpi-icon.excellent { background: #d1fae5; color: #059669; }
.kpi-icon.warning { background: #fef3c7; color: #d97706; }
.kpi-icon.critical { background: #fee2e2; color: #dc2626; }
.kpi-icon.total { background: #e0e7ff; color: #4f46e5; }
.kpi-icon.with-data { background: #dbeafe; color: #2563eb; }
.kpi-icon.no-data { background: #f1f5f9; color: #94a3b8; }

.kpi-data {
  display: flex;
  flex-direction: column;
}

.kpi-value {
  font-size: 1.25rem;
  font-weight: 700;
  color: #1e293b;
  line-height: 1.1;
}

.kpi-item.score-kpi .kpi-value {
  font-size: 1.5rem;
}

.kpi-item.score-kpi.excellent .kpi-value { color: #059669; }
.kpi-item.score-kpi.good .kpi-value { color: #3b82f6; }
.kpi-item.score-kpi.warning .kpi-value { color: #d97706; }
.kpi-item.score-kpi.at-risk .kpi-value { color: #f97316; }
.kpi-item.score-kpi.critical .kpi-value { color: #dc2626; }

.kpi-label {
  font-size: 0.7rem;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  white-space: nowrap;
}

/* Ops briefing purpose cell */
.ops-briefing-panel {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  border-radius: 12px;
  padding: 1rem 1.25rem;
  margin-bottom: 1rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
}

.ops-briefing-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
  margin-bottom: 0.75rem;
}

.ops-briefing-header h2 {
  margin: 0 0 0.25rem;
  font-size: 1.05rem;
  color: #1e293b;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.ops-briefing-sub {
  margin: 0;
  font-size: 0.85rem;
  color: #64748b;
}

.ops-briefing-actions {
  display: flex;
  gap: 0.5rem;
  flex-shrink: 0;
}

.ops-briefing-loading {
  color: #64748b;
  font-size: 0.9rem;
}

.outreach-results {
  border-top: 1px solid #e2e8f0;
  margin-top: 0.75rem;
  padding-top: 0.75rem;
}

.outreach-results-head {
  display: flex;
  align-items: baseline;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.outreach-results-head h3 {
  margin: 0;
  font-size: 0.95rem;
  color: #1e293b;
  display: flex;
  align-items: center;
  gap: 0.4rem;
}

.outreach-window {
  font-size: 0.8rem;
  color: #64748b;
}

.outreach-results-text {
  margin: 0.4rem 0;
  font-size: 0.85rem;
  color: #334155;
}

.outreach-metrics {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem 1rem;
  font-size: 0.8rem;
  color: #475569;
  margin-bottom: 0.5rem;
}

.outreach-metrics .metric-problem {
  color: #c0392b;
}

.outreach-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.ops-cohort-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: 0.5rem;
  margin-bottom: 0.75rem;
}

.ops-cohort-card {
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 0.75rem;
  background: #f8fafc;
  text-align: left;
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s;
}

.ops-cohort-card:hover,
.ops-cohort-card.active {
  border-color: #3b82f6;
  background: #eff6ff;
}

.cohort-count {
  display: block;
  font-size: 1.25rem;
  font-weight: 700;
  color: #1e293b;
}

.cohort-label {
  display: block;
  font-size: 0.75rem;
  color: #64748b;
  margin-top: 0.15rem;
}

.ops-suggested-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.ops-active-filter {
  margin-top: 0.75rem;
  font-size: 0.85rem;
  color: #475569;
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.briefing-report-modal {
  max-width: 640px;
}

.briefing-report-lead {
  font-size: 0.9rem;
  color: #64748b;
  margin-bottom: 1rem;
}

.briefing-preview pre {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 0.75rem;
  font-size: 0.75rem;
  max-height: 280px;
  overflow: auto;
  white-space: pre-wrap;
}

/* Dashboard Grid - Directory + record detail */
.dashboard-grid {
  display: grid;
  grid-template-columns: 280px minmax(0, 1fr);
  gap: 1rem;
  margin-bottom: 1rem;
  align-items: stretch;
  height: calc(100vh - 180px);
  min-height: 500px;
}

.dashboard-grid.dashboard-grid--consult-only {
  grid-template-columns: minmax(0, 1fr);
}

.entity-directory-panel {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  border-radius: 16px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.12);
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
}

.directory-header {
  padding: 1rem 1rem 0.5rem;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}

.directory-header h3 {
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  display: flex;
  align-items: baseline;
  gap: 0.35rem;
}

.directory-count {
  font-size: 0.85rem;
  font-weight: 500;
  color: #64748b;
}

.directory-controls {
  padding: 0.75rem 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}

.directory-search {
  position: relative;
}

.directory-search i {
  position: absolute;
  left: 0.65rem;
  top: 50%;
  transform: translateY(-50%);
  color: #94a3b8;
  font-size: 0.8rem;
}

.directory-search input {
  width: 100%;
  padding: 0.45rem 0.65rem 0.45rem 1.75rem;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 0.875rem;
}

.directory-filter {
  width: 100%;
  padding: 0.45rem 0.65rem;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 0.875rem;
  background: white;
}

.directory-list {
  flex: 1;
  overflow-y: auto;
  padding: 0.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}

.directory-loading,
.directory-empty {
  padding: 1.5rem 1rem;
  text-align: center;
  color: #64748b;
  font-size: 0.875rem;
}

.directory-row {
  display: block;
  width: 100%;
  text-align: left;
  padding: 0.65rem 2.25rem 0.65rem 0.75rem;
  border: none;
  border-left: 3px solid #cbd5e1;
  border-radius: 8px;
  background: #f8fafc;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s;
  position: relative;
}

.directory-row:hover {
  background: #f1f5f9;
}

.directory-row.active {
  background: #eff6ff;
  border-left-color: #3b82f6;
}

.directory-row.inactive {
  opacity: 0.75;
}

.row-main {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 0.5rem;
}

.row-name {
  font-weight: 600;
  font-size: 0.875rem;
  color: #0f172a;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.row-score {
  font-size: 0.8rem;
  font-weight: 600;
  flex-shrink: 0;
}

.row-issue {
  margin: 0.25rem 0 0;
  font-size: 0.75rem;
  color: #64748b;
  line-height: 1.3;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.row-issue--muted {
  font-style: italic;
}

.row-actions {
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  display: flex;
  gap: 0.25rem;
}

.row-action-btn {
  border: none;
  background: transparent;
  color: #64748b;
  cursor: pointer;
  padding: 0.2rem 0.35rem;
  border-radius: 4px;
  font-size: 0.8rem;
}

.row-action-btn:hover {
  color: #4338ca;
  background: #eef2ff;
}

.directory-load-more {
  margin-top: 0.25rem;
  padding: 0.5rem;
  border: 1px dashed #cbd5e1;
  border-radius: 8px;
  background: transparent;
  color: #475569;
  font-size: 0.8rem;
  cursor: pointer;
}

.directory-load-more:disabled {
  opacity: 0.6;
  cursor: wait;
}

@media (max-width: 1400px) {
  .dashboard-grid {
    grid-template-columns: 260px minmax(0, 1fr);
  }
}

@media (max-width: 1200px) {
  .dashboard-grid {
    grid-template-columns: 240px minmax(0, 1fr);
  }
}

@media (max-width: 1000px) {
  .dashboard-grid {
    grid-template-columns: 1fr;
    height: auto;
  }
  .entity-directory-panel {
    max-height: 320px;
  }
}

/* Panel Styles */

/* Detail Panel */
.detail-panel {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  border-radius: 16px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15);
  height: 100%;
  overflow-y: auto;
}

.detail-panel--agent {
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.agent-pane {
  flex: 1;
  min-height: 0;
  height: 100%;
}

.agent-pane :deep(.compliance-consult-chat) {
  height: 100%;
  border: none;
  border-radius: 16px;
}

.back-to-agent {
  margin: 0.75rem 1rem 0;
}

.status-badge {
  padding: 0.125rem 0.5rem;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 500;
}

.status-badge.excellent { background: #d1fae5; color: #059669; }
.status-badge.good { background: #dbeafe; color: #2563eb; }
.status-badge.warning { background: #fef3c7; color: #d97706; }
.status-badge.at-risk { background: #ffedd5; color: #ea580c; }
.status-badge.critical { background: #fee2e2; color: #dc2626; }

.status-badge.large {
  padding: 0.25rem 0.75rem;
  font-size: 0.875rem;
}

.trend-indicator {
  font-size: 0.75rem;
}

.trend-indicator.improving { color: #10b981; }
.trend-indicator.stable { color: #64748b; }
.trend-indicator.declining { color: #ef4444; }

.status-badge.no-data {
  background: #e2e8f0;
  color: #64748b;
}

/* Detail Panel */
.detail-panel {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.12);
  height: 100%;
  overflow-y: auto;
}

.entity-detail {
  padding: 2rem;
}

.detail-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 2rem;
  padding-bottom: 1.5rem;
  border-bottom: 1px solid #e2e8f0;
}

.detail-title h2 {
  margin: 0 0 0.5rem 0;
  color: #1e293b;
  font-size: 1.5rem;
}

.score-circle {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  font-weight: 700;
  color: white;
}

.score-circle.excellent { background: linear-gradient(135deg, #10b981, #059669); }
.score-circle.good { background: linear-gradient(135deg, #3b82f6, #2563eb); }
.score-circle.warning { background: linear-gradient(135deg, #f59e0b, #d97706); }
.score-circle.at-risk { background: linear-gradient(135deg, #f97316, #ea580c); }
.score-circle.critical { background: linear-gradient(135deg, #ef4444, #dc2626); }

.detail-section {
  margin-bottom: 2rem;
}

.detail-section h4 {
  margin: 0 0 1rem 0;
  color: #1e293b;
  font-size: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.detail-section h4 i {
  color: #3b82f6;
}

/* Coverage Grid */
.coverage-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: 1rem;
}

.coverage-item {
  padding: 1rem;
  border-radius: 12px;
  text-align: center;
  background: #f8fafc;
  border: 2px solid transparent;
  transition: all 0.2s ease;
}

.coverage-item.clickable {
  cursor: pointer;
}

.coverage-item.clickable:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.coverage-item.excellent { border-color: #10b981; background: #f0fdf4; }
.coverage-item.good { border-color: #3b82f6; background: #eff6ff; }
.coverage-item.warning { border-color: #f59e0b; background: #fffbeb; }
.coverage-item.critical { border-color: #ef4444; background: #fef2f2; }

.coverage-type {
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 0.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
}

.coverage-doc-icon {
  font-size: 0.875rem;
  opacity: 0.6;
  transition: opacity 0.2s ease;
}

.coverage-item.clickable:hover .coverage-doc-icon {
  opacity: 1;
}

.coverage-score {
  font-size: 1.5rem;
  font-weight: 700;
}

.coverage-status {
  font-size: 0.75rem;
  color: #64748b;
}

.coverage-counts {
  font-size: 0.75rem;
  color: #94a3b8;
  margin-top: 0.25rem;
}

/* Root Causes */
.root-causes-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.root-cause-item {
  padding: 1rem;
  background: #f8fafc;
  border-radius: 12px;
  border-left: 4px solid #e2e8f0;
}

.root-cause-item:has(.cause-type.critical) { border-left-color: #ef4444; }
.root-cause-item:has(.cause-type.high) { border-left-color: #f97316; }
.root-cause-item:has(.cause-type.medium) { border-left-color: #f59e0b; }

.cause-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
}

.cause-type {
  padding: 0.125rem 0.5rem;
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 600;
}

.cause-type.critical { background: #fee2e2; color: #dc2626; }
.cause-type.high { background: #ffedd5; color: #ea580c; }
.cause-type.medium { background: #fef3c7; color: #d97706; }
.cause-type.low { background: #e0e7ff; color: #4f46e5; }

.cause-impact {
  font-size: 0.75rem;
  color: #64748b;
}

.cause-title {
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 0.5rem;
}

.cause-resolution {
  font-size: 0.875rem;
  color: #059669;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

/* Expirations */
.expirations-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.expiration-item {
  display: flex;
  align-items: center;
  padding: 0.75rem 1rem;
  background: #f8fafc;
  border-radius: 8px;
}

.expiration-item.urgent {
  background: #fef2f2;
  border: 1px solid #fecaca;
}

.exp-type {
  font-weight: 600;
  color: #1e293b;
  width: 80px;
}

.exp-date {
  flex: 1;
  color: #64748b;
}

.exp-days {
  font-weight: 600;
  padding: 0.25rem 0.5rem;
  border-radius: 6px;
  background: #e2e8f0;
}

.exp-days.critical { background: #fee2e2; color: #dc2626; }
.exp-days.warning { background: #fef3c7; color: #d97706; }

/* Projections */
.projection-bars {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.projection-item {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.projection-label {
  width: 70px;
  font-size: 0.875rem;
  color: #64748b;
}

.projection-bar {
  flex: 1;
  height: 12px;
  background: #e2e8f0;
  border-radius: 6px;
  overflow: hidden;
}

.projection-fill {
  height: 100%;
  border-radius: 6px;
  transition: width 0.3s;
}

.projection-fill.excellent { background: #10b981; }
.projection-fill.good { background: #3b82f6; }
.projection-fill.warning { background: #f59e0b; }
.projection-fill.at-risk { background: #f97316; }
.projection-fill.critical { background: #ef4444; }

.projection-value {
  width: 50px;
  text-align: right;
  font-weight: 600;
  color: #1e293b;
}

/* AI Summary */
.ai-section .section-header-with-action {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.ai-section .section-header-with-action h4 {
  margin: 0;
}

.btn-sm {
  padding: 0.375rem 0.75rem;
  font-size: 0.8rem;
}

.btn-ai {
  background: linear-gradient(135deg, #8b5cf6, #6366f1);
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.375rem;
  transition: all 0.2s;
}

.btn-ai:hover:not(:disabled) {
  background: linear-gradient(135deg, #7c3aed, #4f46e5);
  transform: translateY(-1px);
}

.btn-ai:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.model-badge {
  font-size: 0.7rem;
  padding: 0.25rem 0.5rem;
  background: #f1f5f9;
  color: #64748b;
  border-radius: 4px;
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.ai-summary {
  padding: 1rem;
  background: linear-gradient(135deg, #eff6ff, #f0fdf4);
  border-radius: 12px;
  color: #1e293b;
  line-height: 1.6;
  border: 1px solid #dbeafe;
}

.ai-summary p {
  margin: 0;
}

.ai-summary .trend-note {
  margin-top: 0.75rem;
  padding-top: 0.75rem;
  border-top: 1px solid #dbeafe;
  font-size: 0.9rem;
  color: #475569;
}

.ai-summary .trend-note i {
  color: #3b82f6;
  margin-right: 0.375rem;
}

.ai-placeholder {
  padding: 2rem;
  text-align: center;
  background: #f8fafc;
  border-radius: 12px;
  color: #64748b;
}

.ai-placeholder i {
  font-size: 2rem;
  margin-bottom: 0.75rem;
  color: #94a3b8;
}

.ai-placeholder p {
  margin: 0;
}

/* Actions */
.actions-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.action-item {
  display: flex;
  gap: 1rem;
  padding: 1rem;
  background: #f8fafc;
  border-radius: 12px;
}

.action-priority {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  color: white;
  flex-shrink: 0;
}

.action-priority.critical { background: #ef4444; }
.action-priority.high { background: #f97316; }
.action-priority.medium { background: #f59e0b; }
.action-priority.low { background: #3b82f6; }

.action-content {
  flex: 1;
}

.action-text {
  font-weight: 500;
  color: #1e293b;
  margin-bottom: 0.25rem;
}

.action-meta {
  display: flex;
  gap: 0.75rem;
  font-size: 0.75rem;
}

.action-owner {
  color: #64748b;
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.action-owner i {
  font-size: 0.65rem;
}

.action-urgency {
  padding: 0.125rem 0.375rem;
  border-radius: 4px;
  font-weight: 600;
}

.action-urgency.critical { background: #fee2e2; color: #dc2626; }
.action-urgency.high { background: #ffedd5; color: #ea580c; }
.action-urgency.medium { background: #fef3c7; color: #d97706; }
.action-urgency.low { background: #dbeafe; color: #2563eb; }

.action-impact {
  color: #64748b;
}

/* Welcome Panel */
.welcome-panel {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  padding: 2rem;
}

.welcome-content {
  text-align: center;
  max-width: 500px;
}

.welcome-icon {
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1.5rem;
}

.welcome-icon i {
  font-size: 2.5rem;
  color: white;
}

.welcome-content h2 {
  margin: 0 0 0.5rem 0;
  color: #1e293b;
  font-size: 1.5rem;
}

.welcome-subtitle {
  color: #64748b;
  margin: 0 0 1.5rem 0;
  font-size: 1rem;
}

.welcome-ask {
  display: flex;
  gap: 0.5rem;
  margin-bottom: 2rem;
}

.welcome-ask-input {
  flex: 1;
  min-width: 0;
  padding: 0.75rem 1rem;
  border: 1px solid #cbd5e1;
  border-radius: 10px;
  font-size: 0.95rem;
  color: #1e293b;
}

.welcome-ask-input:focus {
  outline: 2px solid #3b82f6;
  outline-offset: -1px;
  border-color: #3b82f6;
}

.welcome-ask-btn {
  white-space: nowrap;
}

.welcome-ask-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.welcome-suggestions h4 {
  color: #94a3b8;
  font-size: 0.8rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin: 0 0 1rem 0;
}

.suggestion-cards {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0.75rem;
}

.suggestion-card {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.2s;
}

.suggestion-card:hover {
  background: #eff6ff;
  border-color: #3b82f6;
  transform: translateY(-2px);
}

.suggestion-card i {
  font-size: 1.25rem;
  color: #6366f1;
}

.suggestion-card span {
  font-weight: 500;
  color: #1e293b;
  font-size: 0.9rem;
}

/* Top Issues Panel */
.top-issues-panel {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  border-radius: 16px;
  padding: 1.5rem 2rem;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15);
}

.top-issues-panel h3 {
  margin: 0 0 1rem 0;
  color: #1e293b;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.top-issues-panel h3 i {
  color: #ef4444;
}

.issues-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 1rem;
}

.issue-card {
  padding: 1rem;
  background: #f8fafc;
  border-radius: 12px;
  border-left: 4px solid #ef4444;
}

.issue-type {
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 0.25rem;
}

.issue-count {
  font-size: 1.25rem;
  font-weight: 700;
  color: #ef4444;
}

.issue-entities {
  font-size: 0.75rem;
  color: #64748b;
}

/* Loading & Empty States */
.loading-state,
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem;
  color: #94a3b8;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 3px solid #e2e8f0;
  border-top-color: #3b82f6;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 1rem;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.load-more {
  display: flex;
  justify-content: center;
  padding: 1rem;
}

.btn-outline {
  background: white;
  border: 2px solid #3b82f6;
  color: #3b82f6;
  padding: 0.5rem 1.5rem;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  text-decoration: none;
}

.btn-outline:hover:not(:disabled) {
  background: #3b82f6;
  color: white;
}

/* Empty State Inline */
.empty-state-inline {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 1.5rem;
  background: #f1f5f9;
  border-radius: 12px;
  text-align: center;
  gap: 0.5rem;
}

.empty-state-inline i {
  font-size: 2rem;
  color: #94a3b8;
}

.empty-state-inline p {
  margin: 0;
  color: #475569;
}

.empty-state-inline .hint {
  font-size: 0.85rem;
  color: #64748b;
}

.empty-state-inline.warning {
  background: #fef3c7;
  border: 1px solid #fbbf24;
}

.empty-state-inline.warning i {
  color: #d97706;
}

.empty-state-inline.warning p {
  color: #92400e;
}

.empty-state-inline.success {
  background: #d1fae5;
  border: 1px solid #34d399;
}

.empty-state-inline.success i {
  color: #059669;
}

.empty-state-inline.success p {
  color: #065f46;
}

/* Notification Draft */
/* Unified Action Panel */
.action-panel {
  display: flex;
  flex-direction: column;
  height: 100%;
  background: white;
  border-radius: 12px;
  overflow: hidden;
}

.action-panel-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 1rem;
  background: linear-gradient(135deg, #6366f1, #8b5cf6);
  color: white;
}

.panel-tabs {
  display: flex;
  gap: 0.25rem;
}

.panel-tab {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: rgba(255, 255, 255, 0.1);
  border: none;
  border-radius: 6px;
  color: rgba(255, 255, 255, 0.8);
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.panel-tab:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.2);
  color: white;
}

.panel-tab.active {
  background: white;
  color: #6366f1;
}

.panel-tab:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.tab-badge {
  background: rgba(255, 255, 255, 0.3);
  padding: 0.125rem 0.5rem;
  border-radius: 10px;
  font-size: 0.75rem;
}

.panel-tab.active .tab-badge {
  background: #6366f1;
  color: white;
}

.btn-icon {
  background: rgba(255, 255, 255, 0.1);
  border: none;
  color: white;
  width: 2rem;
  height: 2rem;
  border-radius: 6px;
  cursor: pointer;
  transition: background 0.2s;
}

.btn-icon:hover {
  background: rgba(255, 255, 255, 0.2);
}

.tab-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
}

.results-content .results-inner,
.draft-content .draft-inner {
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 1rem;
  min-height: 0;
  overflow-y: auto;
}

.empty-tab {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #9ca3af;
  padding: 2rem;
}

.empty-tab i {
  font-size: 2.5rem;
  margin-bottom: 1rem;
  opacity: 0.5;
}

.empty-tab p {
  font-size: 0.875rem;
}

/* Results Tab Styles */
.results-summary {
  display: flex;
  gap: 1rem;
  margin-bottom: 1rem;
  padding: 0.5rem 0.75rem;
  background: #f3f4f6;
  border-radius: 6px;
  flex-wrap: wrap;
}

.summary-stat {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  font-size: 0.8rem;
}

.summary-stat i {
  color: #6366f1;
  font-size: 0.75rem;
}

.summary-stat .stat-label {
  color: #6b7280;
}

.summary-stat .stat-value {
  font-weight: 600;
  color: #1f2937;
}

.results-list {
  flex: 1;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
}

/* Phase 2: Entity wrapper for expansion */
.result-entity-wrapper {
  display: flex;
  flex-direction: column;
  gap: 0;
}

.result-entity-wrapper.expanded .result-entity-card {
  border-bottom-left-radius: 0;
  border-bottom-right-radius: 0;
  border-bottom-color: transparent;
}

.result-entity-card {
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  padding: 0.75rem;
  cursor: pointer;
  transition: all 0.15s;
}

.result-entity-card:hover {
  border-color: #6366f1;
  background: #f5f3ff;
}

.result-entity-card.selected {
  border-color: #6366f1;
  background: #ede9fe;
}

.result-entity-card.status-critical {
  border-left: 3px solid #ef4444;
}

.result-entity-card.status-warning {
  border-left: 3px solid #f59e0b;
}

.result-entity-card.status-good {
  border-left: 3px solid #10b981;
}

.entity-main {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

/* Phase 2: Expand button */
.expand-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1.75rem;
  height: 1.75rem;
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  border-radius: 4px;
  cursor: pointer;
  color: #64748b;
  transition: all 0.15s;
  flex-shrink: 0;
}

.expand-btn:hover {
  background: #e2e8f0;
  color: #6366f1;
  border-color: #6366f1;
}

.expand-btn.docs-btn {
  color: #4f46e5;
  background: #eef2ff;
  border-color: #c7d2fe;
}
.expand-btn.docs-btn:hover {
  background: #e0e7ff;
  color: #3730a3;
  border-color: #a5b4fc;
}

.expand-btn.record-btn {
  color: #4338ca;
}

.expand-btn.record-btn:hover {
  background: #eef2ff;
  color: #312e81;
}

.result-entity-wrapper.expanded .expand-btn {
  background: #6366f1;
  color: white;
  border-color: #6366f1;
}

.expand-btn i {
  font-size: 0.7rem;
}

.entity-info {
  cursor: pointer;
}

.entity-checkbox input[type="checkbox"] {
  width: 1rem;
  height: 1rem;
  cursor: pointer;
  accent-color: #6366f1;
}

.entity-info {
  flex: 1;
  min-width: 0;
}

.entity-info .entity-name {
  font-size: 0.875rem;
  font-weight: 600;
  color: #1f2937;
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.entity-meta {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 0.25rem;
  flex-wrap: wrap;
}

.entity-meta .status-badge {
  display: inline-block;
  padding: 0.125rem 0.375rem;
  border-radius: 4px;
  font-weight: 500;
  text-transform: uppercase;
  font-size: 0.6rem;
}

.entity-meta .meta-id {
  font-size: 0.7rem;
  color: #6b7280;
  background: #f3f4f6;
  padding: 0.125rem 0.375rem;
  border-radius: 4px;
}

.entity-meta .status-badge.critical, 
.entity-meta .status-badge.failed { background: #fef2f2; color: #dc2626; }
.entity-meta .status-badge.needs_attention,
.entity-meta .status-badge.warning { background: #fffbeb; color: #d97706; }
.entity-meta .status-badge.excellent,
.entity-meta .status-badge.compliant,
.entity-meta .status-badge.passed { background: #ecfdf5; color: #059669; }
.entity-meta .status-badge.unknown { background: #f3f4f6; color: #6b7280; }

.entity-issues {
  margin-top: 0.5rem;
  padding-top: 0.5rem;
  border-top: 1px dashed #e5e7eb;
}

.issues-list {
  display: flex;
  gap: 0.25rem;
  flex-wrap: wrap;
}

.issue-tag {
  font-size: 0.65rem;
  padding: 0.125rem 0.375rem;
  background: #fef2f2;
  color: #991b1b;
  border-radius: 3px;
}

.more-issues {
  font-size: 0.65rem;
  padding: 0.125rem 0.375rem;
  background: #e5e7eb;
  color: #6b7280;
  border-radius: 3px;
}

/* Phase 1 Enhanced Entity Display Styles */
.entity-header-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.5rem;
}

.entity-header-row .entity-name {
  flex: 1;
  min-width: 0;
}

.score-badge {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.25rem 0.5rem;
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 700;
  flex-shrink: 0;
}

.score-badge.excellent { background: #d1fae5; color: #059669; }
.score-badge.good { background: #dbeafe; color: #2563eb; }
.score-badge.warning { background: #fef3c7; color: #d97706; }
.score-badge.at-risk { background: #ffedd5; color: #ea580c; }
.score-badge.critical { background: #fee2e2; color: #dc2626; }

.trend-indicator {
  display: inline-flex;
  align-items: center;
  font-size: 0.7rem;
}

.trend-indicator.improving { color: #10b981; }
.trend-indicator.stable { color: #64748b; }
.trend-indicator.declining { color: #ef4444; }

.coverage-summary {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.125rem 0.375rem;
  border-radius: 4px;
  font-size: 0.7rem;
  font-weight: 500;
}

.coverage-summary i {
  font-size: 0.6rem;
}

.coverage-summary.coverage-excellent { background: #d1fae5; color: #059669; }
.coverage-summary.coverage-good { background: #dbeafe; color: #2563eb; }
.coverage-summary.coverage-warning { background: #fef3c7; color: #d97706; }
.coverage-summary.coverage-critical { background: #fee2e2; color: #dc2626; }

.reqs-summary {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 0.7rem;
  color: #64748b;
  background: #f3f4f6;
  padding: 0.125rem 0.375rem;
  border-radius: 4px;
}

.reqs-summary i {
  font-size: 0.6rem;
  color: #9ca3af;
}

.issues-header {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  margin-bottom: 0.375rem;
  font-size: 0.7rem;
  color: #dc2626;
  font-weight: 500;
}

.issues-header i {
  font-size: 0.65rem;
}

.issue-tag.issue-critical { background: #fee2e2; color: #991b1b; border-left: 2px solid #dc2626; }
.issue-tag.issue-high { background: #ffedd5; color: #9a3412; border-left: 2px solid #ea580c; }
.issue-tag.issue-medium { background: #fef3c7; color: #92400e; border-left: 2px solid #d97706; }
.issue-tag.issue-low { background: #f3f4f6; color: #4b5563; border-left: 2px solid #9ca3af; }

.coverage-details {
  display: flex;
  flex-wrap: wrap;
  gap: 0.375rem;
  margin-top: 0.5rem;
  padding-top: 0.5rem;
  border-top: 1px dashed #e5e7eb;
}

.coverage-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.125rem 0.375rem;
  border-radius: 4px;
  font-size: 0.6rem;
  font-weight: 500;
}

.coverage-chip .coverage-type {
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.coverage-chip .coverage-status-icon {
  font-size: 0.55rem;
}

.coverage-chip.chip-excellent { background: #d1fae5; color: #059669; }
.coverage-chip.chip-good { background: #dbeafe; color: #2563eb; }
.coverage-chip.chip-warning { background: #fef3c7; color: #d97706; }
.coverage-chip.chip-critical { background: #fee2e2; color: #dc2626; }

/* End Phase 1 Enhanced Styles */

.no-results {
  text-align: center;
  padding: 2rem;
  color: #6b7280;
}

.no-results i {
  font-size: 2rem;
  color: #9ca3af;
  margin-bottom: 0.5rem;
}

.results-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 0.75rem;
  border-top: 1px solid #e5e7eb;
}

.select-actions {
  display: flex;
  gap: 0.375rem;
}

.btn-sm {
  padding: 0.375rem 0.625rem;
  font-size: 0.75rem;
}

/* Draft Tab Styles */
.draft-recipients {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
  padding: 0.5rem 0.75rem;
  background: #f3f4f6;
  border-radius: 6px;
}

.recipients-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.draft-recipients label {
  font-weight: 500;
  color: #6b7280;
  font-size: 0.8rem;
}

.recipients-display {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex: 1;
  flex-wrap: wrap;
}

.recipient-count {
  font-weight: 600;
  color: #1f2937;
  font-size: 0.875rem;
}

.recipient-warning {
  font-size: 0.75rem;
  font-weight: 600;
  color: #b45309;
  background: #fef3c7;
  border-radius: 4px;
  padding: 0.1rem 0.4rem;
}

.recipient-loading {
  font-size: 0.75rem;
  color: #6b7280;
}

.recipient-address-list {
  list-style: none;
  margin: 0;
  padding: 0;
  max-height: 160px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.recipient-address {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 0.75rem;
  font-size: 0.8rem;
  padding: 0.25rem 0.5rem;
  background: #ffffff;
  border-left: 3px solid #10b981;
  border-radius: 4px;
}

.recipient-address.no-address {
  border-left-color: #f59e0b;
  background: #fffbeb;
}

.recipient-entity {
  font-weight: 600;
  color: #1f2937;
}

.recipient-email {
  color: #374151;
  text-align: right;
}

.recipient-email small {
  display: block;
  color: #6b7280;
  font-size: 0.7rem;
}

.recipient-email.is-missing {
  color: #b45309;
}

.recipient-address-empty {
  margin: 0;
  font-size: 0.8rem;
  color: #b45309;
}

.btn-link {
  background: none;
  border: none;
  color: #6366f1;
  cursor: pointer;
  font-size: 0.75rem;
}

.btn-link:hover {
  text-decoration: underline;
}

.ai-controls-compact {
  display: flex;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
  flex-wrap: wrap;
}

.control-select {
  padding: 0.375rem 0.5rem;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  font-size: 0.75rem;
  background: white;
}

.btn-ai {
  background: linear-gradient(135deg, #8b5cf6, #6366f1);
  color: white;
  border: none;
  padding: 0.375rem 0.75rem;
  border-radius: 6px;
  font-size: 0.75rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.375rem;
}

.btn-ai:hover:not(:disabled) {
  opacity: 0.9;
}

.draft-field {
  margin-bottom: 0.75rem;
}

.draft-field label {
  display: block;
  font-size: 0.75rem;
  font-weight: 500;
  color: #6b7280;
  margin-bottom: 0.25rem;
}

.draft-field .subject-input {
  width: 100%;
  padding: 0.5rem 0.75rem;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  font-size: 0.875rem;
}

.draft-field .subject-input:focus {
  outline: none;
  border-color: #6366f1;
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
}

.message-field {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.message-field .message-editor {
  flex: 1;
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  font-size: 0.875rem;
  font-family: inherit;
  resize: none;
  min-height: 150px;
}

.message-field .message-editor:focus {
  outline: none;
  border-color: #6366f1;
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
}

.draft-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 0.75rem;
  border-top: 1px solid #e5e7eb;
  margin-top: 0.75rem;
}

.ai-badge-small {
  font-size: 0.7rem;
  color: #8b5cf6;
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.btn-lg {
  padding: 0.625rem 1.25rem;
  font-size: 0.9rem;
}

/* Legacy Query Results Panel - kept for backward compatibility */
.query-results-panel {
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow-y: auto;
}

.results-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1.25rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #e5e7eb;
}

.results-header .header-left h2 {
  font-size: 1.25rem;
  color: #1f2937;
  margin: 0 0 0.25rem 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.results-header .header-left h2 i {
  color: #6366f1;
}

.results-header .header-left p {
  color: #6b7280;
  font-size: 0.875rem;
  margin: 0;
}

.results-header .header-right {
  display: flex;
  gap: 0.5rem;
}

.results-summary {
  display: flex;
  gap: 1.5rem;
  margin-bottom: 1.25rem;
  padding: 0.75rem 1rem;
  background: #f3f4f6;
  border-radius: 8px;
}

.summary-stat {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
}

.summary-stat i {
  color: #6366f1;
  font-size: 0.875rem;
}

.summary-stat .stat-label {
  color: #6b7280;
}

.summary-stat .stat-value {
  font-weight: 600;
  color: #1f2937;
}

.results-list {
  flex: 1;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-bottom: 1rem;
}

.result-entity-card {
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 1rem;
  transition: all 0.2s;
}

.result-entity-card:hover {
  border-color: #6366f1;
  box-shadow: 0 2px 8px rgba(99, 102, 241, 0.1);
}

.result-entity-card.status-critical {
  border-left: 3px solid #ef4444;
}

.result-entity-card.status-warning {
  border-left: 3px solid #f59e0b;
}

.result-entity-card.status-good {
  border-left: 3px solid #10b981;
}

.entity-main {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
}

.entity-checkbox {
  padding-top: 0.125rem;
}

.entity-checkbox input[type="checkbox"] {
  width: 1rem;
  height: 1rem;
  cursor: pointer;
  accent-color: #6366f1;
}

.entity-info {
  flex: 1;
}

.entity-info .entity-name {
  font-size: 0.95rem;
  font-weight: 600;
  color: #1f2937;
  margin: 0 0 0.25rem 0;
}

.entity-meta {
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.entity-meta .meta-item {
  font-size: 0.75rem;
  color: #6b7280;
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.entity-meta .status-badge {
  padding: 0.125rem 0.5rem;
  border-radius: 4px;
  font-weight: 500;
  text-transform: uppercase;
  font-size: 0.65rem;
}

.entity-meta .status-badge.critical,
.entity-meta .status-badge.failed {
  background: #fef2f2;
  color: #dc2626;
}

.entity-meta .status-badge.needs_attention,
.entity-meta .status-badge.warning {
  background: #fffbeb;
  color: #d97706;
}

.entity-meta .status-badge.excellent,
.entity-meta .status-badge.compliant {
  background: #ecfdf5;
  color: #059669;
}

.entity-issues {
  margin-top: 0.75rem;
  padding-top: 0.75rem;
  border-top: 1px dashed #e5e7eb;
}

.issues-label {
  font-size: 0.75rem;
  color: #ef4444;
  font-weight: 500;
  margin-bottom: 0.5rem;
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.issues-list {
  display: flex;
  gap: 0.375rem;
  flex-wrap: wrap;
}

.issue-tag {
  font-size: 0.7rem;
  padding: 0.25rem 0.5rem;
  background: #fef2f2;
  color: #991b1b;
  border-radius: 4px;
}

.more-issues {
  font-size: 0.7rem;
  padding: 0.25rem 0.5rem;
  background: #f3f4f6;
  color: #6b7280;
  border-radius: 4px;
}

.no-results {
  text-align: center;
  padding: 3rem 2rem;
  color: #6b7280;
}

.no-results i {
  font-size: 3rem;
  color: #10b981;
  margin-bottom: 1rem;
}

.no-results p {
  margin: 0.5rem 0;
}

.no-results .hint {
  font-size: 0.875rem;
  color: #9ca3af;
}

.results-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 1rem;
  border-top: 1px solid #e5e7eb;
}

.select-actions {
  display: flex;
  gap: 0.5rem;
}

.action-buttons {
  display: flex;
  gap: 0.5rem;
  align-items: center;
}

.action-buttons .btn-secondary {
  background: #f3f4f6;
  color: #374151;
  border: 1px solid #d1d5db;
}

.action-buttons .btn-secondary:hover {
  background: #e5e7eb;
}

.btn-sm {
  padding: 0.375rem 0.75rem;
  font-size: 0.75rem;
}

/* End Query Results Panel */

.notification-draft {
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow-y: auto;
}

.draft-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 2rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #e2e8f0;
}

.draft-header h2 {
  margin: 0 0 0.25rem 0;
  color: #1e293b;
  font-size: 1.5rem;
}

.draft-header p {
  margin: 0;
  color: #64748b;
  font-size: 0.9rem;
}

.draft-envelope {
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0; /* Allow flex shrinking */
  overflow: visible; /* Let content be visible and clickable */
}

.envelope-row {
  display: grid;
  grid-template-columns: 80px 1fr;
  padding: 1rem 1.5rem;
  border-bottom: 1px solid #f1f5f9;
  align-items: center;
}

.envelope-row label {
  font-weight: 600;
  color: #64748b;
  font-size: 0.875rem;
}

.envelope-row label .edit-hint {
  font-weight: 400;
  font-size: 0.7rem;
  color: #94a3b8;
  margin-left: 0.25rem;
}

.copy-subcontractor-control {
  display: flex;
  align-items: flex-start;
  gap: 0.6rem;
  padding: 0.8rem 1.5rem;
  border-bottom: 1px solid #f1f5f9;
  color: #334155;
  cursor: pointer;
}

.copy-subcontractor-control input {
  margin-top: 0.2rem;
}

.copy-subcontractor-control span {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  font-size: 0.82rem;
}

.copy-subcontractor-control small {
  color: #64748b;
}

.recipient-details {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.recipient-tag {
  background: #eff6ff;
  color: #3b82f6;
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 0.4rem;
  border: 1px solid #dbeafe;
}

.recipient-tag small {
  color: #1e40af;
  font-weight: 400;
}

.recipient-tag.no-address {
  background: #fffbeb;
  color: #b45309;
  border-color: #fde68a;
}

.recipient-tag small.is-missing {
  color: #b45309;
}

.recipient-summary {
  font-size: 0.9rem;
  color: #1e293b;
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.category-badge {
  background: #f1f5f9;
  color: #475569;
  padding: 0.125rem 0.5rem;
  border-radius: 4px;
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
}

.from-field, .subject-field {
  font-size: 0.9375rem;
  color: #1e293b;
}

.envelope-body {
  padding: 1rem;
  display: flex;
  flex-direction: column;
}

.body-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  font-weight: 600;
  color: #64748b;
  margin-bottom: 0.5rem;
}

.body-label i {
  color: #6366f1;
}

.body-label .edit-hint {
  font-weight: 400;
  font-size: 0.75rem;
  color: #94a3b8;
}

.message-editor {
  width: 100%;
  min-height: 200px;
  height: auto;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 1rem 1.25rem;
  font-family: inherit;
  font-size: 1rem;
  line-height: 1.6;
  color: #334155;
  resize: vertical;
  background: white;
  transition: border-color 0.2s, box-shadow 0.2s;
  box-sizing: border-box;
}

.message-editor:hover {
  border-color: #cbd5e1;
}

.message-editor:focus {
  outline: none;
  border-color: #6366f1;
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
}

.message-editor::placeholder {
  color: #94a3b8;
}

/* AI Email Generation Controls */
.ai-controls {
  display: flex;
  align-items: flex-end;
  gap: 1rem;
  margin-bottom: 1rem;
  padding: 1rem;
  background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
  border-radius: 10px;
  border: 1px solid #bae6fd;
  flex-shrink: 0;
  position: relative;
  z-index: 1;
}

.ai-options {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  flex: 1;
}

.ai-options .option-group {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.ai-options .option-group.flex-1 {
  flex: 1;
  min-width: 200px;
}

.ai-options label {
  font-size: 0.75rem;
  font-weight: 600;
  color: #0369a1;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.ai-options select,
.ai-options .instructions-input {
  padding: 0.5rem 0.75rem;
  border: 1px solid #7dd3fc;
  border-radius: 6px;
  font-size: 0.875rem;
  background: white;
}

.ai-options select:focus,
.ai-options .instructions-input:focus {
  outline: none;
  border-color: #0ea5e9;
  box-shadow: 0 0 0 3px rgba(14, 165, 233, 0.1);
}

.btn-ai {
  padding: 0.625rem 1.25rem;
  background: linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%);
  color: white;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  white-space: nowrap;
  transition: all 0.2s;
}

.btn-ai:hover:not(:disabled) {
  background: linear-gradient(135deg, #7c3aed 0%, #4f46e5 100%);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(99, 102, 241, 0.3);
}

.btn-ai:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.subject-input {
  width: 100%;
  padding: 0.5rem 0.75rem;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-size: 1rem;
  font-weight: 500;
  color: #1e293b;
  background: white;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.subject-input:hover {
  border-color: #cbd5e1;
}

.subject-input:focus {
  outline: none;
  border-color: #6366f1;
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
}

.ai-badge {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%);
  color: #166534;
  font-size: 0.75rem;
  font-weight: 600;
  border-top: 1px solid #bbf7d0;
}

.ai-badge i {
  color: #22c55e;
}

.ai-badge .generation-time {
  color: #16a34a;
  font-weight: 400;
}

.draft-actions {
  margin-top: 1rem;
  padding-top: 1rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-shrink: 0;
}

.warning-note {
  font-size: 0.875rem;
  color: #64748b;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.warning-note i {
  color: #3b82f6;
}

.btn-lg {
  padding: 0.75rem 2rem;
  font-size: 1rem;
  border-radius: 10px;
}

/* Responsive */
@media (max-width: 768px) {
  .compliance-health-dashboard {
    padding: 0.75rem;
  }
  
  .kpi-bar {
    padding: 0.5rem 0.75rem;
  }
  
  .kpi-item {
    padding: 0.25rem 0.5rem;
  }
  
  .header-content {
    flex-direction: column;
    gap: 0.5rem;
    align-items: flex-start;
  }
  
  .header-title {
    flex-direction: column;
    align-items: flex-start;
  }
  
  .header-subtitle {
    margin-left: 0;
    padding-left: 0;
    border-left: none;
  }
  
  .detail-panel {
    max-height: 400px;
  }
}

/* Create Entity Modal */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  backdrop-filter: blur(4px);
}

.create-entity-modal {
  background: white;
  border-radius: 16px;
  width: 90%;
  max-width: 520px;
  max-height: 90vh;
  overflow: hidden;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid #e2e8f0;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
}

.modal-header h3 {
  margin: 0;
  font-size: 1.125rem;
  font-weight: 600;
  color: #1e293b;
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.modal-header h3 i {
  color: #6366f1;
}

.btn-close {
  background: none;
  border: none;
  padding: 0.5rem;
  cursor: pointer;
  color: #64748b;
  border-radius: 8px;
  transition: all 0.2s;
}

.btn-close:hover {
  background: #f1f5f9;
  color: #1e293b;
}

.modal-body {
  padding: 1.5rem;
  overflow-y: auto;
  max-height: calc(90vh - 160px);
}

.form-group {
  margin-bottom: 1rem;
}

.form-group label {
  display: block;
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
  margin-bottom: 0.5rem;
}

.form-group label .required {
  color: #ef4444;
}

.form-row {
  display: flex;
  gap: 1rem;
}

.form-row .form-group {
  flex: 1;
}

.form-row .form-group.flex-2 {
  flex: 2;
}

.form-input,
.form-select {
  width: 100%;
  padding: 0.625rem 0.875rem;
  font-size: 0.875rem;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  background: white;
  color: #1e293b;
  transition: all 0.2s;
}

.form-input:focus,
.form-select:focus {
  outline: none;
  border-color: #6366f1;
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
}

.form-input::placeholder {
  color: #9ca3af;
}

.form-section {
  margin-top: 1.25rem;
  padding-top: 1.25rem;
  border-top: 1px solid #e2e8f0;
}

.section-label {
  display: block;
  font-size: 0.75rem;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 0.75rem;
}

.checkbox-group {
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid #e2e8f0;
}

.checkbox-label {
  display: flex !important;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  margin-bottom: 0.25rem !important;
}

.checkbox-label input[type="checkbox"] {
  width: 16px;
  height: 16px;
  accent-color: #6366f1;
  cursor: pointer;
  flex-shrink: 0;
}

.checkbox-text {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  font-weight: 500;
  color: #374151;
}

.checkbox-text i {
  color: #6366f1;
  font-size: 0.8125rem;
}

.field-hint {
  font-size: 0.75rem;
  color: #6b7280;
  margin: 0.25rem 0 0 1.5rem;
  line-height: 1.4;
}

.info-note {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  background: #f0f9ff;
  border: 1px solid #bae6fd;
  border-radius: 8px;
  font-size: 0.8125rem;
  color: #0369a1;
  margin-top: 1rem;
}

.info-note i {
  margin-top: 2px;
  flex-shrink: 0;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  padding: 1rem 1.5rem;
  border-top: 1px solid #e2e8f0;
  background: #f8fafc;
}

/* Coverage Documents Modal */
.coverage-docs-modal {
  background: white;
  border-radius: 16px;
  width: 90%;
  max-width: 700px;
  max-height: 90vh;
  overflow: hidden;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
}

.coverage-docs-modal .doc-count {
  font-size: 0.875rem;
  color: #6b7280;
  font-weight: 400;
  margin-left: 0.5rem;
}

.documents-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.document-item {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  padding: 1rem;
  background: #f8fafc;
  border-radius: 12px;
  border: 2px solid transparent;
  transition: all 0.2s ease;
}

.document-item:hover {
  background: #f1f5f9;
  border-color: #e2e8f0;
}

.document-item.passed {
  border-left: 4px solid #10b981;
}

.document-item.failed {
  border-left: 4px solid #ef4444;
}

.doc-icon {
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  font-size: 1.25rem;
}

.document-item.passed .doc-icon {
  background: #d1fae5;
  color: #10b981;
}

.document-item.failed .doc-icon {
  background: #fee2e2;
  color: #ef4444;
}

.doc-info {
  flex: 1;
  min-width: 0;
}

.doc-name {
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 0.25rem;
  word-break: break-word;
}

.doc-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  font-size: 0.8125rem;
  color: #64748b;
  margin-bottom: 0.5rem;
}

.doc-type,
.doc-date,
.doc-requirement {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
}

.doc-type {
  padding: 0.125rem 0.5rem;
  background: #e0e7ff;
  color: #4f46e5;
  border-radius: 4px;
  font-weight: 500;
}

.doc-evidence {
  font-size: 0.8125rem;
  color: #475569;
  font-style: italic;
  padding: 0.5rem;
  background: white;
  border-radius: 6px;
  margin-top: 0.5rem;
}

.doc-evidence i {
  color: #94a3b8;
  margin-right: 0.25rem;
}

.doc-actions {
  flex-shrink: 0;
}

.loading-state,
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 1.5rem;
  text-align: center;
  color: #64748b;
}

.loading-state i {
  font-size: 2rem;
  color: #6366f1;
  margin-bottom: 1rem;
}

.empty-state i {
  font-size: 3rem;
  color: #cbd5e1;
  margin-bottom: 1rem;
}

.empty-state p {
  margin: 0;
  font-size: 0.9375rem;
}

.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.625rem 1.25rem;
  font-size: 0.875rem;
  font-weight: 500;
  border-radius: 8px;
  border: none;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-secondary {
  background: #f1f5f9;
  color: #475569;
}

.btn-secondary:hover {
  background: #e2e8f0;
}

.btn-primary {
  background: linear-gradient(135deg, #6366f1 0%, #4f46e5 100%);
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background: linear-gradient(135deg, #4f46e5 0%, #4338ca 100%);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(99, 102, 241, 0.3);
}

.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-success {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: white;
  text-decoration: none;
}

.btn-success:hover {
  background: linear-gradient(135deg, #059669 0%, #047857 100%);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
}

/* Bulk Import Modal Styles */
.bulk-import-modal {
  max-width: 900px;
  max-height: 90vh;
  overflow-y: auto;
}

.import-step {
  padding: 1.5rem;
}

.step-header {
  margin-bottom: 1.5rem;
}

.step-header h4 {
  margin: 0 0 0.5rem 0;
  color: #1e293b;
  font-size: 1.25rem;
}

.step-subtitle {
  margin: 0;
  color: #64748b;
  font-size: 0.9375rem;
}

/* Format Examples */
.format-examples {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.example-card {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 1rem;
}

.example-title {
  font-weight: 600;
  color: #475569;
  margin-bottom: 0.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
}

.example-card code {
  display: block;
  background: white;
  padding: 0.75rem;
  border-radius: 4px;
  font-size: 0.75rem;
  line-height: 1.5;
  color: #334155;
  white-space: pre-wrap;
  word-break: break-word;
}

/* Bulk Input Textarea */
.bulk-input-textarea {
  width: 100%;
  padding: 1rem;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  font-family: 'Courier New', monospace;
  font-size: 0.875rem;
  line-height: 1.6;
  resize: vertical;
  transition: border-color 0.2s;
}

.bulk-input-textarea:focus {
  outline: none;
  border-color: #6366f1;
}

.input-hint {
  margin-top: 0.5rem;
  font-size: 0.8125rem;
  color: #64748b;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.input-hint i {
  color: #f59e0b;
}

/* Parse Summary */
.parse-summary {
  display: flex;
  gap: 1rem;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
}

.summary-stat {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  border-radius: 8px;
  font-weight: 500;
  font-size: 0.875rem;
}

.summary-stat.success {
  background: #d1fae5;
  color: #065f46;
}

.summary-stat.warning {
  background: #fef3c7;
  color: #92400e;
}

.summary-stat.error {
  background: #fee2e2;
  color: #991b1b;
}

/* Errors Section */
.errors-section,
.duplicates-section,
.valid-rows-section {
  margin-bottom: 1.5rem;
}

.errors-section h5,
.duplicates-section h5,
.valid-rows-section h5 {
  margin: 0 0 1rem 0;
  color: #1e293b;
  font-size: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.error-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.error-item {
  background: #fef2f2;
  border-left: 3px solid #ef4444;
  padding: 0.75rem;
  border-radius: 4px;
  font-size: 0.875rem;
}

.error-row {
  font-weight: 600;
  color: #991b1b;
  margin-right: 0.5rem;
}

.error-message {
  color: #dc2626;
}

.error-raw {
  display: block;
  margin-top: 0.5rem;
  padding: 0.5rem;
  background: white;
  border-radius: 4px;
  font-size: 0.75rem;
  color: #64748b;
}

/* Duplicates */
.duplicate-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.duplicate-item {
  background: #fffbeb;
  border: 1px solid #fbbf24;
  border-radius: 8px;
  padding: 1rem;
}

.duplicate-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.75rem;
}

.duplicate-similarity {
  font-weight: 600;
  color: #92400e;
  font-size: 0.875rem;
}

.already-tracked-badge {
  background: #10b981;
  color: white;
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.duplicate-comparison {
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  gap: 1rem;
  margin-bottom: 0.75rem;
}

.comparison-col {
  background: white;
  padding: 0.75rem;
  border-radius: 6px;
}

.col-label {
  font-size: 0.75rem;
  text-transform: uppercase;
  font-weight: 600;
  color: #64748b;
  margin-bottom: 0.5rem;
}

.entity-name {
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 0.25rem;
}

.entity-details {
  font-size: 0.8125rem;
  color: #64748b;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.comparison-arrow {
  display: flex;
  align-items: center;
  color: #94a3b8;
  font-size: 1.25rem;
}

.duplicate-actions {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.btn-sm {
  padding: 0.5rem 1rem;
  font-size: 0.8125rem;
}

/* Valid Rows */
.valid-rows-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  max-height: 400px;
  overflow-y: auto;
}

.valid-row-item {
  background: #f0fdf4;
  border: 1px solid #86efac;
  border-radius: 8px;
  padding: 0.75rem;
  display: flex;
  gap: 0.75rem;
}

.row-icon {
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  background: #10b981;
  color: white;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
}

.row-info {
  flex: 1;
}

.row-name {
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 0.25rem;
}

.row-details {
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
  align-items: center;
}

.detail-badge {
  background: #dbeafe;
  color: #1e40af;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.detail-text {
  font-size: 0.8125rem;
  color: #475569;
}

.more-rows {
  text-align: center;
  padding: 0.75rem;
  background: #f8fafc;
  border-radius: 8px;
  color: #64748b;
  font-weight: 500;
  font-size: 0.875rem;
}

/* Import Results */
.import-results {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.result-stat {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: white;
  padding: 1.5rem;
  border-radius: 12px;
  display: flex;
  align-items: center;
  gap: 1rem;
}

.result-stat i {
  font-size: 2rem;
  opacity: 0.9;
}

.stat-info {
  flex: 1;
}

.stat-value {
  font-size: 2rem;
  font-weight: 700;
  line-height: 1;
  margin-bottom: 0.25rem;
}

.stat-label {
  font-size: 0.875rem;
  opacity: 0.9;
}

.import-errors {
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 8px;
  padding: 1rem;
}

.import-errors h5 {
  margin: 0 0 0.75rem 0;
  color: #991b1b;
  font-size: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

/* Create Entity Modal - Additional Sections */
.form-section {
  margin-bottom: 1.5rem;
  padding-bottom: 1.5rem;
  border-bottom: 1px solid #e2e8f0;
}

.form-section:last-of-type {
  border-bottom: none;
}

.section-title {
  margin: 0 0 1rem 0;
  color: #1e293b;
  font-size: 1rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.section-title i {
  color: #6366f1;
}

.agent-actions {
  margin-top: 0.5rem;
}

.btn-link {
  background: none;
  border: none;
  color: #6366f1;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0;
  transition: color 0.2s;
}

.btn-link:hover {
  color: #4f46e5;
  text-decoration: underline;
}

.inline-form {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 1rem;
  margin-top: 0.75rem;
}

.inline-form .form-row {
  margin-bottom: 0.75rem;
}

.inline-form .btn {
  width: 100%;
}

/* Bulk Import - Agent Assignment Section */
.agent-assignment-section {
  background: #f0f9ff;
  border: 1px solid #bae6fd;
  border-radius: 8px;
  padding: 1rem;
  margin-bottom: 1.5rem;
}

.agent-assignment-section h5 {
  margin: 0 0 0.5rem 0;
  color: #0c4a6e;
  font-size: 0.9375rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.section-help {
  margin: 0 0 0.75rem 0;
  font-size: 0.8125rem;
  color: #475569;
}
</style>

